import type { BrandPalette } from '../types';
import { FONT_FAMILIES } from '../types';
import { LAB_COMPONENTS } from './labComponents';

export type KnobKind =
  | 'color'
  | 'radius'
  | 'shadow'
  | 'fontFamily'
  | 'fontWeight'
  | 'fontSize'
  | 'letterSpacing'
  | 'opacity'
  | 'number'
  /** Free-form string value — used for variant-style enum switches
   *  (e.g. `BottomActionStyle.style ∈ {'disc','pill'}`). */
  | 'text'
  /** Image URL written into a CSS `background-image`. The operator
   *  pastes a raw URL (e.g. `https://cdn.example.com/foo.jpg`); the
   *  formatter auto-wraps it with `url("…")` so the var is a valid
   *  CSS image value. Pre-wrapped `url(...)` / `linear-gradient(...)` /
   *  `none` values are passed through untouched. */
  | 'imageUrl';

export type KnobDefault =
  | { kind: 'literal'; value: string | number }
  | { kind: 'role'; role: keyof BrandPalette }
  /** Default = current resolved value of another component's knob. Used
   *  for cascade defaults: page-specific backgrounds inherit from the
   *  global PageBackground, which itself falls back to palette via
   *  `role`. Matches the CSS fallback chain so placeholder text in the
   *  editor always shows the same colour the user actually sees on the
   *  page. */
  | { kind: 'componentKnob'; component: string; knob: string };

export interface KnobDef {
  kind: KnobKind;
  label: string;
  default: KnobDefault;
  /** Optional one-line explanation rendered under the control. The label
   *  column is narrow and truncates, so anything the operator can't guess
   *  from a short label belongs here instead of in parentheses inside the
   *  label. */
  hint?: string;
  /** When set, the knob renders as a select-dropdown of these values
   *  instead of a free-form text input. Use for font-family pickers and
   *  any other small enum where we want to constrain the operator to a
   *  pre-approved list (e.g. `FONT_FAMILIES`). */
  options?: readonly string[];
  /** Same as `ComponentSchema.templates`, for one knob of a shared
   *  component: the editor hides it on every other template. */
  templates?: readonly ComponentTemplate[];
  /** What the knob falls back to on a given template when that template
   *  paints the element differently (sleek's own CSS defaults / `var()`
   *  fallbacks). Keeps the editor's placeholder and swatch equal to what
   *  the page actually shows; `default` covers every other template. */
  templateDefaults?: Partial<Record<ComponentTemplate, KnobDefault>>;
}

export const TEMPLATE_NAMES = ['classic', 'modern', 'sleek', 'glass'] as const;
export type ComponentTemplate = (typeof TEMPLATE_NAMES)[number];

export const TEMPLATE_LABELS: Record<ComponentTemplate, string> = {
  classic: 'Classic',
  modern: 'Modern',
  sleek: 'Sleek',
  glass: 'Glass',
};

export function isComponentTemplate(value: unknown): value is ComponentTemplate {
  return TEMPLATE_NAMES.some((name) => name === value);
}

/** True when `key` is one of `variant`'s knobs on a per-variant component
 *  (`glass-iconFill` belongs to `glass-icon`, not to `glass`): slot names
 *  start with a capital, variant names stay all-lowercase. */
export function isVariantKnob(key: string, variant: string): boolean {
  if (!key.startsWith(variant)) return false;
  const first = key[variant.length];
  return first !== undefined && first === first.toUpperCase() && first !== first.toLowerCase();
}

/** Drops the knobs another template owns (a sleek-only knob on a shared
 *  component), so e.g. modern operators never see controls modern ignores.
 *  A per-variant component also loses the variants left without knobs, so
 *  the picker never offers an empty one. A component without such knobs
 *  comes back as the same object. */
export function withTemplateKnobs(c: ComponentSchema, template: ComponentTemplate | undefined): ComponentSchema {
  if (template === undefined) return c;
  const entries = Object.entries(c.knobs);
  const kept = entries.filter(([, k]) => k.templates === undefined || k.templates.includes(template));
  if (kept.length === entries.length) return c;
  const knobs = Object.fromEntries(kept);
  if (!c.variants || c.variantKnob) return { ...c, knobs };
  const variants = c.variants.filter((v) => kept.some(([key]) => isVariantKnob(key, v)));
  return { ...c, knobs, variants };
}

/** The component with `scenes` as `template` renders it. */
export function withTemplateScenes(c: ComponentSchema, template: ComponentTemplate | undefined): ComponentSchema {
  const scenes = template && c.templateScenes?.[template];
  return scenes ? { ...c, scenes } : c;
}

/** The default the editor shows for a knob on `template`. */
export function knobDefault(knob: KnobDef, template: ComponentTemplate | undefined): KnobDefault {
  return (template && knob.templateDefaults?.[template]) ?? knob.default;
}

/**
 * Scenes where a registry component is actually rendered. Mirrors the
 * route the iframe is currently on. `useIframeScene` reads the iframe's
 * pathname and the sidebar filters COMPONENTS down to entries whose
 * `scenes` includes it (or that have no `scenes` field at all — those
 * are cross-cutting, e.g. global PageBackground).
 *
 *   pack         — Pack page (`/`)
 *   just-pulled  — Just Pulled list (`/just-pulled`)
 *   my-packs     — My Packs page (`/my-packs`)
 *   reveal       — Reveal flow (only visible after RIP — operator can't
 *                  navigate to it from the constructor; kept so reveal-
 *                  only components stay in the schema)
 *   custom-pack  — The custom pack lab. It shares `/` with the pack page,
 *                  so the iframe reports it with a synthetic pathname
 *                  (`CUSTOM_PACK_ROUTE`) when the launched game is a custom pack.
 */
export type Scene = 'pack' | 'just-pulled' | 'my-packs' | 'reveal' | 'custom-pack';

export interface ComponentSchema {
  /** Technical identifier — drives `--co-<name>-<knob>` CSS variable names.
   *  Must never be renamed without coordinated changes to existing themes. */
  name: string;
  /** Optional human-friendly label shown in the Design Constructor UI.
   *  Falls back to `name` when omitted. Pure presentation — does not affect
   *  CSS vars or storage keys. */
  displayName?: string;
  group: 'Buttons' | 'Badges' | 'Panels' | 'Misc' | 'Layout' | 'Modals' | 'Reveal';
  /** Scenes in which the component is actually visible. `undefined` means
   *  "shown in every scene" — the editor renders it under every section.
   *  Pure presentation, never affects CSS vars. */
  scenes?: Scene[];
  /** Scenes a cross-cutting component (no `scenes`) is left out of because
   *  nothing there renders it (the DS Button on the Custom Pack Lab). Pure
   *  presentation, like `scenes`. */
  excludeScenes?: Scene[];
  /** Sub-heading the component is listed under inside a scene group that
   *  orders its components into sections (the Custom Pack Lab's
   *  `LAB_SECTIONS`). Elsewhere it has no effect. */
  section?: string;
  /** One line on where the component is on the page: the sidebar shows it
   *  as the section's tooltip, and the search matches it. */
  description?: string;
  /** `scenes` for one template that renders the component somewhere else
   *  too (sleek's Vault card page reuses the winner screen's title and
   *  value), so the other templates' sidebars stay as they are. */
  templateScenes?: Partial<Record<ComponentTemplate, Scene[]>>;
  /** Allowed values for a variant picker. First entry = default selection.
   *  Used by knobs of kind `text` that act as enum-style switches (e.g.
   *  `BottomActionShell.gradientDirection ∈ {'to bottom','to right'}`). */
  variants?: string[];
  /** Which knob the variant picker writes its selection into. Defaults to
   *  `'style'` for backwards compatibility. */
  variantKnob?: string;
  /** Sidebar label rendered above the variant radios. Defaults to
   *  `'Variant'`. */
  variantLabel?: string;
  /** If set, the component is only rendered by these templates — editor
   *  filters it out for any other. `undefined` means every template renders
   *  it. A list because sleek is a fork of modern and shares its parts. */
  templates?: readonly ComponentTemplate[];
  knobs: Record<string, KnobDef>;
}

// Common shortcut helpers to reduce repetition.
const role = (r: keyof BrandPalette): KnobDefault => ({ kind: 'role', role: r });
const lit = (v: string | number): KnobDefault => ({ kind: 'literal', value: v });
/** A toast's on/off switch (`--ui-notification-*` in the iframe's index.css). */
const NOTIFICATION_VISIBILITY = ['shown', 'hidden'] as const;
// Scope for knobs and components sleek never renders, so its editor
// doesn't offer controls that change nothing on the page. Glass is a fork of
// modern, so it renders them too.
const NOT_SLEEK: readonly ComponentTemplate[] = ['classic', 'modern', 'glass'];
const inherit = (component: string, knob: string): KnobDefault => ({
  kind: 'componentKnob',
  component,
  knob,
});

// CSS `text-transform` values exposed via Button's per-variant Text-case
// knob. Drives a select dropdown in the Design Constructor sidebar.
const TEXT_TRANSFORM_OPTIONS = ['none', 'uppercase', 'lowercase', 'capitalize'] as const;

// Factory for the three My Packs KPI cards (Packs Opened / Total
// Estimated Value / Total Buyback Value) — identical knob sets, one
// registry entry per card. See the call sites next to `MyPacksStatsBar`.
const myPacksStatCard = (name: string, statLabel: string): ComponentSchema => ({
  name,
  displayName: `My Packs — KPI card (${statLabel})`,
  group: 'Panels',
  scenes: ['my-packs'],
  knobs: {
    bgFrom: {
      kind: 'color',
      label: 'Background — start (mobile pill)',
      default: inherit('MyPacksStatsBar', 'statMobileFill'),
      templates: NOT_SLEEK,
    },
    bgTo: {
      kind: 'color',
      label: 'Background — end (mobile pill)',
      default: inherit('MyPacksStatsBar', 'statMobileFill'),
      templates: NOT_SLEEK,
    },
    borderColor: {
      kind: 'color',
      label: 'Border colour (mobile pill)',
      default: lit('transparent'),
      templates: NOT_SLEEK,
    },
    borderWidth: {
      kind: 'radius',
      label: 'Border width (px, mobile pill)',
      default: lit(0),
      templates: NOT_SLEEK,
    },
    borderRadius: {
      kind: 'radius',
      label: 'Border radius (px, mobile pill)',
      default: lit(12),
      templates: NOT_SLEEK,
    },
    labelColor: {
      kind: 'color',
      label: 'Label colour',
      default: inherit('MyPacksStatsBar', 'valueColor'),
    },
    valueColor: {
      kind: 'color',
      label: 'Value colour',
      default: inherit('MyPacksStatsBar', 'valueColor'),
    },
  },
});

export const COMPONENTS: readonly ComponentSchema[] = [
  // --- Buttons -------------------------------------------------------------
  {
    name: 'Button',
    displayName: 'Generic Button (DS)',
    group: 'Buttons',
    // No `scenes` field = cross-cutting. The DS `<Button>` renders on
    // multiple routes (Pack page RIP / Rip-Repackz on JustPulled / Sell
    // actions on MyPacks), so anchoring it to one scene would hide it
    // from the sidebar exactly when the operator clicks it. The Custom
    // Pack Lab draws its own buttons (LabCta, LabGhostButton, …).
    excludeScenes: ['custom-pack'],
    // Per-variant theming. Each of the 8 visual variants owns its own
    // {fill,text,border} triplet. `variantKnob` is intentionally omitted —
    // the picker runs in UI-only mode (see VariantPicker.value/onChange):
    // changing the variant only filters which knobs are visible, it does
    // NOT write a CSS variable. The `variants` list and the knob-key
    // prefixes must stay in lock-step with `Button.tsx` and with the
    // ComponentSection `key.startsWith(currentVariant)` filter.
    variants: [
      'primary',
      'secondary',
      'ghost',
      'icon',
      'glass',
      'glass-frosted',
      'glass-icon',
      'accent-gloss',
      'header-icon',
    ],
    variantLabel: 'Button variant',
    knobs: {
      primaryFill: { kind: 'color', label: 'Fill', default: role('primary'), templateDefaults: { sleek: lit('#FFFFFF') } },
      primaryText: { kind: 'color', label: 'Text colour', default: role('textOnBrand'), templateDefaults: { sleek: lit('#101114') } },
      primaryBorder: { kind: 'color', label: 'Border', default: lit('transparent') },
      secondaryFill: { kind: 'color', label: 'Fill', default: role('surfaceBg') },
      secondaryText: { kind: 'color', label: 'Text colour', default: role('textPrimary') },
      secondaryBorder: { kind: 'color', label: 'Border', default: role('surfaceBorder') },
      ghostFill: { kind: 'color', label: 'Fill', default: lit('transparent') },
      ghostText: { kind: 'color', label: 'Text colour', default: role('textPrimary') },
      ghostBorder: { kind: 'color', label: 'Border', default: lit('transparent') },
      iconFill: { kind: 'color', label: 'Fill', default: role('surfaceBg'), templates: NOT_SLEEK },
      iconText: { kind: 'color', label: 'Icon colour', default: role('textPrimary'), templates: NOT_SLEEK },
      // `icon` renders borderless by default; width defaults to 0 so the
      // border stays invisible until the operator sets iconBorderWidth.
      // (Default changed from role('surfaceBorder') → transparent now that
      // the slot is actually consumed — a surface-coloured ring would have
      // appeared the moment the variant grew a border.)
      iconBorder: { kind: 'color', label: 'Border', default: lit('transparent'), templates: NOT_SLEEK },
      glassFill: { kind: 'color', label: 'Fill', default: lit('rgba(255,255,255,0.1)'), templates: NOT_SLEEK },
      glassText: { kind: 'color', label: 'Text colour', default: lit('#FFFFFF'), templates: NOT_SLEEK },
      glassBorder: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.25)'), templates: NOT_SLEEK },
      // glass-frosted — Figma 305:26637 two-layer tint, 27px blur,
      // softer hairline border + drop-shadow. Used by My Packs / Delivery
      // headers' "Home" pill and the "+ Add Delivery" CTA.
      //
      // Schema default is a parseable solid colour (the dominant tint of
      // the Figma gradient) so the ColorField swatch + alpha slider stay
      // enabled — the picker disables itself for values it can't parse
      // (gradients, var(), currentColor). The CSS-level fallback in
      // Button.tsx still renders the full two-layer gradient when no
      // override is set, so an un-overridden button looks unchanged.
      'glass-frostedFill': {
        kind: 'color',
        label: 'Fill',
        default: lit('rgba(255,255,255,0.20)'),
      },
      'glass-frostedText': { kind: 'color', label: 'Text colour', default: lit('#FFFFFF') },
      'glass-frostedBorder': { kind: 'color', label: 'Border', default: lit('rgba(171,174,179,0.24)') },
      'glass-iconFill': { kind: 'color', label: 'Fill', default: lit('rgba(255,255,255,0.1)'), templates: NOT_SLEEK },
      'glass-iconText': { kind: 'color', label: 'Icon colour', default: lit('#FFFFFF'), templates: NOT_SLEEK },
      'glass-iconBorder': { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.25)'), templates: NOT_SLEEK },
      'accent-glossFill': { kind: 'color', label: 'Fill', default: role('accent'), templateDefaults: { sleek: lit('#FFFFFF') } },
      'accent-glossText': { kind: 'color', label: 'Text colour', default: role('textOnBrand'), templateDefaults: { sleek: lit('#101114') } },
      'accent-glossBorder': { kind: 'color', label: 'Border', default: lit('transparent') },
      'header-iconFill': { kind: 'color', label: 'Fill', default: lit('#FFFFFF'), templates: NOT_SLEEK },
      'header-iconText': { kind: 'color', label: 'Icon colour', default: role('textPrimary'), templates: NOT_SLEEK },
      'header-iconBorder': { kind: 'color', label: 'Border', default: lit('transparent'), templates: NOT_SLEEK },

      // ── Border width (px) — per variant. Defaults match today: 1px on
      //    bordered variants, 0 on `icon` (renders borderless). ──────────
      primaryBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      secondaryBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      ghostBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      iconBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(0), templates: NOT_SLEEK },
      glassBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1), templates: NOT_SLEEK },
      'glass-frostedBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      'glass-iconBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1), templates: NOT_SLEEK },
      'accent-glossBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      'header-iconBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1), templates: NOT_SLEEK },

      // ── Shadow (box-shadow) — per variant. One field covers both inner
      //    (`inset …`) and drop shadows. Defaults = exact current literals
      //    so an un-overridden button is pixel-identical. ────────────────
      primaryShadow: { kind: 'shadow', label: 'Shadow', default: lit('inset 0 -6px 44px rgba(255,255,255,0.59), inset 0 -6px 14px rgba(255,255,255,0.19)'), templateDefaults: { sleek: lit('none') } },
      secondaryShadow: { kind: 'shadow', label: 'Shadow', default: lit('0 1px 2px #1919191A') },
      ghostShadow: { kind: 'shadow', label: 'Shadow', default: lit('none') },
      iconShadow: { kind: 'shadow', label: 'Shadow', default: lit('0 0 1px rgba(0,0,0,0.25)'), templates: NOT_SLEEK },
      glassShadow: { kind: 'shadow', label: 'Shadow', default: lit('none'), templates: NOT_SLEEK },
      'glass-frostedShadow': { kind: 'shadow', label: 'Shadow', default: lit('0 64px 64px 0 rgba(0,0,0,0.05)') },
      // 27px backdrop-blur is the visual signature of the frosted-glass
      // chip from Figma 305:26637 — strong enough that the layer behind
      // it is unmistakably softened but still legible. Operators can
      // dial it down (or to 0 for an opaque pill) when the surface
      // behind the button isn't busy enough to warrant the blur cost.
      'glass-frostedBlur': { kind: 'radius', label: 'Backdrop blur (px)', default: lit(27) },
      'glass-iconShadow': { kind: 'shadow', label: 'Shadow', default: lit('none'), templates: NOT_SLEEK },
      // accent-gloss keeps its built-in bright inner gloss (a fixed overlay
      // element); this knob adds an OPTIONAL box-shadow on the button, off
      // (none) by default.
      'accent-glossShadow': { kind: 'shadow', label: 'Shadow', default: lit('none') },
      'header-iconShadow': { kind: 'shadow', label: 'Shadow', default: lit('0 9px 30px 0 rgba(7,10,18,0.08)'), templates: NOT_SLEEK },

      // ── Font family — text-bearing variants only. Default Inter; runtime
      //    falls back to `inherit` (= today's behaviour) when unset. ──────
      primaryFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      secondaryFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      ghostFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      glassFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES, templates: NOT_SLEEK },
      'glass-frostedFontFamily': { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      'accent-glossFontFamily': { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },

      // ── Border radius (px) — per variant. Defaults reproduce today's
      //    rounded-* (pill ≈ 9999, icon = --radius-button = 24, gloss = 28). ─
      primaryBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999), templateDefaults: { sleek: lit(12) } },
      secondaryBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      ghostBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      iconBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(24), templates: NOT_SLEEK },
      glassBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999), templates: NOT_SLEEK },
      'glass-frostedBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      'glass-iconBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(9999), templates: NOT_SLEEK },
      'accent-glossBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(28), templateDefaults: { sleek: lit(12) } },
      'header-iconBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(9999), templates: NOT_SLEEK },

      // ── Font size (px) — text variants only. Default 15 (body); runtime
      //    falls back to the size-appropriate token when left unset. ───────
      primaryFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      secondaryFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      ghostFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      glassFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15), templates: NOT_SLEEK },
      'glass-frostedFontSize': { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      'accent-glossFontSize': { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },

      // ── Font weight — text variants only. Default 600 (matches the old
      //    type-*-semi utilities). ────────────────────────────────────────
      primaryFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      secondaryFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      ghostFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      glassFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600), templates: NOT_SLEEK },
      'glass-frostedFontWeight': { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      'accent-glossFontWeight': { kind: 'fontWeight', label: 'Font weight', default: lit(600) },

      // ── Text case — text variants only. Default `none` so each button
      //    renders the literal label its JSX passes; partners switch to
      //    `uppercase`/`lowercase`/`capitalize` per variant when their
      //    brand calls for it. Rendered as a select via KnobControl when
      //    `options` is set. ─────────────────────────────────────────────
      primaryTextTransform: {
        kind: 'text',
        label: 'Text case',
        default: lit('none'),
        options: TEXT_TRANSFORM_OPTIONS,
      },
      secondaryTextTransform: {
        kind: 'text',
        label: 'Text case',
        default: lit('none'),
        options: TEXT_TRANSFORM_OPTIONS,
      },
      ghostTextTransform: {
        kind: 'text',
        label: 'Text case',
        default: lit('none'),
        options: TEXT_TRANSFORM_OPTIONS,
      },
      glassTextTransform: {
        kind: 'text',
        label: 'Text case',
        default: lit('none'),
        options: TEXT_TRANSFORM_OPTIONS,
        templates: NOT_SLEEK,
      },
      'glass-frostedTextTransform': {
        kind: 'text',
        label: 'Text case',
        default: lit('none'),
        options: TEXT_TRANSFORM_OPTIONS,
      },
      'accent-glossTextTransform': {
        kind: 'text',
        label: 'Text case',
        default: lit('none'),
        options: TEXT_TRANSFORM_OPTIONS,
      },

      // Ghost-only underline switch. Lives outside the variant TextCase
      // block because it's a different CSS property (text-decoration) and
      // only this variant ever renders as a "text link" alternative —
      // primary/secondary/etc. should never underline.
      ghostTextDecoration: {
        kind: 'text',
        label: 'Underline',
        default: lit('none'),
        options: ['none', 'underline'] as const,
      },
    },
  },
  {
    name: 'TabButton',
    displayName: 'Tab button',
    group: 'Buttons',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    knobs: {
      activeFill: { kind: 'color', label: 'Active background', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Active text colour', default: role('textPrimary') },
      idleFill: { kind: 'color', label: 'Idle background', default: lit('transparent') },
      idleText: { kind: 'color', label: 'Idle text colour', default: role('textPrimary') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(60) },
    },
  },

  // --- Badges --------------------------------------------------------------
  {
    name: 'CountBadge',
    displayName: 'Tier badge — count chip',
    group: 'Badges',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    knobs: {
      activeFill: { kind: 'color', label: 'Active fill', default: role('accent') },
      activeText: { kind: 'color', label: 'Active text', default: role('textOnBrand') },
      idleFill: { kind: 'color', label: 'Idle fill', default: lit('#FFFFFF') },
      idleText: { kind: 'color', label: 'Idle text', default: role('textPrimary') },
    },
  },
  {
    name: 'RarityBadge',
    // The sticker plate ("RARE") hanging off the photo area on
    // JustPulledPage cards. The chips' inline gradient text is styled from
    // the `JustPulled` section instead (chipRareTextGradient*), so this
    // section only appears where the sticker actually renders.
    displayName: 'Rarity badge (RARE)',
    group: 'Badges',
    templates: NOT_SLEEK,
    scenes: ['just-pulled'],
    knobs: {
      stickerGradientFrom: { kind: 'color', label: 'Sticker — gradient start', default: lit('#E99F1F') },
      stickerGradientTo: { kind: 'color', label: 'Sticker — gradient end', default: lit('#FFFFFF') },
      stickerBorder: { kind: 'color', label: 'Sticker border', default: lit('rgba(255,255,255,0.38)') },
      stickerText: { kind: 'color', label: 'Sticker text', default: role('textOnBrand') },
      borderRadius: { kind: 'radius', label: 'Sticker corner radius (px)', default: lit(10) },
    },
  },
  {
    name: 'ExpectedValueBadge',
    displayName: 'EV badge ($25.00)',
    group: 'Badges',
    scenes: ['pack'],
    templates: ['classic'],
    knobs: {
      bgTint: { kind: 'color', label: 'Background tint', default: lit('rgba(37,234,30,0.22)') },
      text: { kind: 'color', label: 'Text', default: lit('#25ea1e') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(8) },
    },
  },
  {
    name: 'TierDot',
    displayName: 'Tier badge — coloured dot',
    group: 'Badges',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    knobs: {
      baseColor: { kind: 'color', label: 'Base colour', default: role('accent') },
    },
  },
  {
    name: 'ShippingItemStatus',
    displayName: 'Delivery — per-card status badge',
    group: 'Badges',
    scenes: ['my-packs'],
    // Sleek has no per-card badge, so it only keeps the refunded colours (its
    // shipment warning text). The other knobs stay as the fallback the sleek
    // ShippingStatus pill reads for themes saved before it existed.
    knobs: {
      shippedBg: { kind: 'color', label: 'Shipped — background', default: lit('rgba(34,225,28,0.18)'), templates: NOT_SLEEK, templateDefaults: { sleek: lit('rgba(22,163,74,0.08)') } },
      shippedFg: { kind: 'color', label: 'Shipped — text', default: lit('#22E11C'), templates: NOT_SLEEK, templateDefaults: { sleek: lit('#16A34A') } },
      refundedBg: { kind: 'color', label: 'Refunded — background', default: lit('rgba(255,176,32,0.20)') },
      refundedFg: { kind: 'color', label: 'Refunded — text', default: lit('#FFB020') },
      pendingBg: { kind: 'color', label: 'Processing — background', default: lit('rgba(255,255,255,0.16)'), templates: NOT_SLEEK, templateDefaults: { sleek: lit('#25262C') } },
      pendingFg: { kind: 'color', label: 'Processing — text', default: lit('rgba(255,255,255,0.78)'), templates: NOT_SLEEK, templateDefaults: { sleek: lit('#6D707C') } },
      canceledBg: { kind: 'color', label: 'Canceled — background', default: lit('rgba(255,255,255,0.16)'), templates: NOT_SLEEK, templateDefaults: { sleek: lit('rgba(255,255,255,0.06)') } },
      canceledFg: { kind: 'color', label: 'Canceled — text', default: lit('rgba(255,255,255,0.78)'), templates: NOT_SLEEK, templateDefaults: { sleek: lit('rgba(255,255,255,0.40)') } },
    },
  },
  {
    name: 'ShippingStatus',
    displayName: 'Delivery — status pill',
    group: 'Badges',
    scenes: ['my-packs'],
    knobs: {
      pendingBg: { kind: 'color', label: 'Processing — background', default: lit('rgba(255,255,255,0.10)'), templateDefaults: { sleek: inherit('ShippingItemStatus', 'pendingBg') } },
      pendingFg: { kind: 'color', label: 'Processing — text', default: lit('rgba(255,255,255,0.85)'), templateDefaults: { sleek: inherit('ShippingItemStatus', 'pendingFg') } },
      shippedBg: { kind: 'color', label: 'Shipped — background', default: lit('#23D11D'), templateDefaults: { sleek: inherit('ShippingItemStatus', 'shippedBg') } },
      shippedFg: { kind: 'color', label: 'Shipped — text', default: lit('#0E1B12'), templateDefaults: { sleek: inherit('ShippingItemStatus', 'shippedFg') } },
      canceledBg: { kind: 'color', label: 'Canceled — background', default: lit('rgba(255,255,255,0.06)'), templateDefaults: { sleek: inherit('ShippingItemStatus', 'canceledBg') } },
      canceledFg: { kind: 'color', label: 'Canceled — text', default: lit('rgba(255,255,255,0.40)'), templateDefaults: { sleek: inherit('ShippingItemStatus', 'canceledFg') } },
      refundedBg: { kind: 'color', label: 'Refunded — background', hint: 'Also used for Partially Shipped.', default: inherit('ShippingItemStatus', 'refundedBg') },
      refundedFg: { kind: 'color', label: 'Refunded — text', hint: 'Also used for Partially Shipped.', default: inherit('ShippingItemStatus', 'refundedFg') },
    },
  },
  {
    name: 'SoldStatusPanel',
    displayName: 'Sold-out status panel',
    group: 'Badges',
    templates: NOT_SLEEK,
    scenes: ['reveal'],
    knobs: {
      accent: { kind: 'color', label: 'Accent (border + text)', default: role('success') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'BuybackPricePill',
    displayName: 'Buyback price pill',
    group: 'Badges',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: lit('#F2F2F2') },
      text: { kind: 'color', label: 'Text', default: role('textPrimary') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(50) },
    },
  },
  {
    name: 'PotentialHitPricePill',
    displayName: 'Potential-hits price pill',
    group: 'Badges',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: role('surfaceBg') },
      text: { kind: 'color', label: 'Text', default: role('textPrimary') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
    },
  },

  // --- Panels --------------------------------------------------------------
  {
    name: 'GlassPanel',
    displayName: 'Glass overlay card',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['reveal'],
    knobs: {
      tint: { kind: 'color', label: 'Glass tint', default: lit('rgba(29,30,31,0.2)') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.07)') },
      radius: { kind: 'radius', label: 'Corner radius (px)', default: lit(28) },
    },
  },
  {
    name: 'HeroBackdrop',
    displayName: 'Hero backdrop (sonar)',
    group: 'Panels',
    scenes: ['pack'],
    knobs: {
      tint: {
        kind: 'color',
        label: 'Animation tint (overlay blend)',
        default: lit('rgba(0,0,0,0)'),
      },
    },
  },

  // --- Custom template components (not in DS but configurable) ------------
  {
    name: 'RipButton',
    displayName: 'Buy button (RIP disc)',
    group: 'Buttons',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    knobs: {
      discFill: { kind: 'color', label: 'Disc fill', default: role('primary') },
      discText: { kind: 'color', label: 'Disc label colour', default: lit('#FFFFFF') },
      outerCollar: { kind: 'color', label: 'Outer collar', default: lit('#FFFFFF') },
      pillText: { kind: 'color', label: 'Price text colour', default: lit('#FFFFFF') },
      // Value must stay a CSS display keyword; it is written straight onto
      // the "1 Pack" line. With it gone the price centres in the pill.
      countDisplay: {
        kind: 'text',
        label: 'Quantity line (none = hidden)',
        hint: 'The "1 Pack" line above the price. Hide it together with the quantity stepper; the price then sits centred in the pill.',
        default: lit('block'),
        options: ['block', 'none'] as const,
        templates: ['modern', 'glass'],
      },
      // Moves the disc along the bar, e.g. into the space a hidden stepper
      // leaves. Value must stay a `justify-content` keyword: a px offset
      // alone can't keep the disc centred across phone widths.
      align: {
        kind: 'text',
        label: 'Position (flex-end = right, flex-start = left)',
        hint: 'Where the RIP button sits on the bar, mobile and desktop. Fine-tune with the offsets below.',
        default: lit('flex-end'),
        options: ['flex-end', 'center', 'flex-start'] as const,
        templates: ['modern'],
      },
      offsetX: {
        kind: 'radius',
        label: 'Offset X (px, + = right)',
        default: lit(0),
        templates: ['modern'],
      },
      offsetY: {
        kind: 'radius',
        label: 'Offset Y (px, + = down)',
        default: lit(0),
        templates: ['modern'],
      },
    },
  },
  {
    name: 'DemoRipButton',
    displayName: 'Free demo button',
    group: 'Buttons',
    scenes: ['pack'],
    knobs: {
      fillFrom: { kind: 'color', label: 'Fill — left', default: role('primary'), templateDefaults: { sleek: lit('#1A1B1F') } },
      fillTo: {
        kind: 'color',
        label: 'Fill — right',
        // Unset, the right stop is a lighter mix of the LEFT one (see
        // DemoRipButton.tsx), so setting a single colour re-skins the whole
        // pill. The editor inherits the same source so the swatch shown here
        // is the colour the gradient is actually built from.
        default: inherit('DemoRipButton', 'fillFrom'),
        hint: 'Unset = a lighter shade of Fill — left. Set it to pick the end colour yourself.',
      },
      text: { kind: 'color', label: 'Label colour', default: role('textOnBrand') },
      iconDisc: { kind: 'color', label: 'Play disc', default: lit('#FFFFFF') },
      icon: { kind: 'color', label: 'Play arrow', default: role('primary'), templateDefaults: { sleek: lit('#101114') } },
      radius: { kind: 'radius', label: 'Corner radius (px)', default: lit(999) },
      shadow: { kind: 'shadow', label: 'Shadow', default: lit('0 8px 20px 0 rgba(34,225,28,0.40)'), templateDefaults: { sleek: lit('none') } },
    },
  },

  // --- Misc ----------------------------------------------------------------
  {
    name: 'Header',
    displayName: 'Top header bar',
    group: 'Misc',
    scenes: ['pack'],
    knobs: {
      bg: { kind: 'color', label: 'Background', default: role('heroFrom') },
      text: { kind: 'color', label: 'Text', default: role('textOnBrand') },
    },
  },
  {
    name: 'CategoryPill',
    displayName: 'Category pill (Lolitas / Golf / …)',
    group: 'Misc',
    scenes: ['pack'],
    knobs: {
      // `activeFill` historically drives the active-state ACCENT (2px
      // border + outer glow) — kept under that name so existing partner
      // themes don't break. The actual surface fill for the active state
      // lives in `activeBackground` below.
      activeFill: { kind: 'color', label: 'Active accent (border + glow)', default: role('primary'), templateDefaults: { sleek: lit('transparent') } },
      activeText: { kind: 'color', label: 'Active text', default: role('textOnBrand'), templateDefaults: { sleek: lit('#101114') } },
      // Surface fill for the active state. Schema default is a parseable
      // solid colour so the ColorField swatch + alpha slider stay
      // enabled (gradients can't be parsed); the CSS-level fallback in
      // CategoryPill.tsx still applies the full two-layer GLASS_SURFACE
      // gradient when no override is set, so an un-overridden active
      // pill renders identically to before.
      activeBackground: {
        kind: 'color',
        label: 'Active background',
        default: lit('rgba(255,255,255,0.20)'),
        templateDefaults: { sleek: lit('#FFFFFF') },
      },
      idleFill: { kind: 'color', label: 'Idle fill', default: lit('transparent'), templateDefaults: { sleek: lit('#1A1B1F') } },
      idleText: { kind: 'color', label: 'Idle text', default: role('textOnBrand') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(60) },
    },
  },
  {
    name: 'CategoryDropdownPill',
    displayName: 'Category dropdown (mobile)',
    group: 'Misc',
    scenes: ['pack'],
    templates: ['modern', 'sleek', 'glass'],
    knobs: {
      activeFill: { kind: 'color', label: 'Selected row fill', default: lit('rgba(255,255,255,0.15)') },
      activeText: { kind: 'color', label: 'Selected row text', default: lit('#FFFFFF') },
      idleText: { kind: 'color', label: 'Idle / trigger text', default: lit('#FFFFFF') },
      tickColor: { kind: 'color', label: 'Selected tick colour', default: lit('#FFFFFF') },
    },
  },
  {
    // Drives `--co-Loader-color`, consumed by the brand loader's own
    // stylesheet (design-system/Loader/Loader.css). Cross-cutting: no
    // `scenes`, the loader shows on app start, on the buy and buyback
    // overlays and while the reveal screen loads.
    //
    // One knob covers the whole mark: the travelling spot, the static
    // outline behind it and the squircle backplate are all the same colour
    // at different opacities. Left unset, the loader keeps inheriting the
    // colour of whatever it sits on.
    name: 'Loader',
    displayName: 'Loader (app-wide)',
    group: 'Misc',
    knobs: {
      color: { kind: 'color', label: 'Loader colour', default: lit('#FFFFFF') },
    },
  },

  // --- New Plan-10 entries (Constructor partner roles) --------------------
  {
    name: 'PageBackground',
    displayName: 'Page background — global',
    group: 'Layout',
    // No `scenes` field = cross-cutting; shows under every scene group
    // (a partner-wide default for any page that doesn't override).
    knobs: {
      gradientFrom: { kind: 'color', label: 'Top / outer colour', default: role('pageBgFrom') },
      gradientTo: { kind: 'color', label: 'Bottom / inner colour', default: role('pageBgTo') },
    },
  },
  {
    // Global page-container insets. Consumed once by `#root` in
    // apps/iframe/src/index.css (via `--page-safe-top` / `--page-safe-bottom`,
    // which the sticky page headers bleed into and the fixed bottom bars
    // offset themselves by), so these knobs cover Pack, My Packs, Just
    // Pulled, Delivery and Reveal at once. Cross-cutting: no `scenes`, shows
    // under every scene group.
    name: 'PageLayout',
    displayName: 'Page container — safe area',
    group: 'Layout',
    knobs: {
      paddingTop: {
        kind: 'radius',
        label: 'Top inset',
        hint: 'Pushes every page down from the top edge (px). A sticky header stretches up into the strip, so its glass reaches the edge while its controls stay below it. For hosts that draw their own UI over the top of the iframe — iPhone notch, status bar. The iframe cannot read the host app safe area on its own, so set it by hand.',
        default: lit(0),
      },
      paddingBottom: {
        kind: 'radius',
        label: 'Bottom inset',
        hint: 'Pushes every page up from the bottom edge (px) and lifts the fixed bottom bars (buy bar, My Packs toolbar) by the same amount. For hosts that draw their own bottom navigation or a home-indicator bar over the iframe.',
        default: lit(0),
      },
      bottomInsetCover: {
        // Feeds `display` on Backdrop's bottom safe-area strip, so the value
        // must stay a CSS display keyword — same trick as
        // PackPageBackground.sonarDisplay and Scrollbar.style.
        kind: 'text',
        label: 'Bottom inset — cover',
        hint: 'block = the bottom strip is filled with the page background, so content scrolling past it stays hidden. none = the strip stays see-through. No effect while Bottom inset is 0.',
        default: lit('block'),
        options: ['block', 'none'] as const,
      },
    },
  },
  {
    // Document-wide scrollbar (page + any scrollable panel). The knobs
    // drive `--co-Scrollbar-*` consumed by the global scrollbar rules in
    // apps/iframe/src/index.css. Cross-cutting: no `scenes`, shows everywhere.
    name: 'Scrollbar',
    displayName: 'Scrollbar (page-wide)',
    group: 'Layout',
    knobs: {
      thumb: {
        kind: 'color',
        label: 'Thumb colour',
        default: lit('rgba(148,148,148,0.45)'),
      },
      thumbHover: {
        kind: 'color',
        label: 'Thumb colour (hover)',
        default: lit('rgba(148,148,148,0.65)'),
      },
      track: {
        kind: 'color',
        label: 'Track colour',
        default: lit('rgba(0,0,0,0.2)'),
      },
      width: {
        // Feeds `::-webkit-scrollbar { width }` — Chrome/Edge/Safari only
        // (Firefox has no px scrollbar sizing; it uses `style` below).
        kind: 'radius',
        label: 'Width (px, 0 = hide)',
        default: lit(8),
      },
      style: {
        // Firefox fallback — feeds `scrollbar-width`, so the values must
        // be its keywords: `thin` = styled scrollbar, `none` = hidden.
        // Firefox can't size scrollbars in px, hence a separate keyword
        // knob instead of the numeric `width` above.
        kind: 'text',
        label: 'Width in Firefox (thin / none = hide)',
        default: lit('thin'),
        options: ['thin', 'none'] as const,
      },
    },
  },
  {
    name: 'PackPageBackground',
    displayName: 'Page background — Pack page',
    group: 'Layout',
    scenes: ['pack'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top / outer colour (overrides global)',
        default: inherit('PageBackground', 'gradientFrom'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom / inner colour (overrides global)',
        default: inherit('PageBackground', 'gradientTo'),
      },
      sonarDisplay: {
        // On/off switch for the sonar-waves (rings) backdrop behind the pack.
        // The value goes straight into `display` on the wrapper, so it must
        // stay a CSS display keyword — same trick as Scrollbar.style.
        kind: 'text',
        label: 'Sonar rings behind pack (none = hide)',
        default: lit('block'),
        options: ['block', 'none'] as const,
        templateDefaults: { sleek: lit('none') },
      },
    },
  },
  {
    name: 'JustPulledCard',
    displayName: 'Just Pulled card',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['just-pulled'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      // ── Title (e.g. "2019 Green Swirl Cards Kendrick Nunn ...") ───────
      titleColor: { kind: 'color', label: 'Title — colour', default: lit('#FFFFFF') },
      titleFont: {
        kind: 'fontFamily',
        label: 'Title — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(15) },

      // ── Offer name (e.g. "NEW WEB3") ──────────────────────────────────
      offerNameColor: { kind: 'color', label: 'Offer name — colour', default: lit('#FFFFFF') },
      offerNameFont: {
        kind: 'fontFamily',
        label: 'Offer name — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      offerNameSize: { kind: 'fontSize', label: 'Offer name — size (px)', default: lit(13) },

      // ── Time ago (e.g. "1 mo ago") ────────────────────────────────────
      timeAgoColor: { kind: 'color', label: 'Time ago — colour', default: lit('rgba(255,255,255,0.6)') },
      timeAgoFont: {
        kind: 'fontFamily',
        label: 'Time ago — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      timeAgoSize: { kind: 'fontSize', label: 'Time ago — size (px)', default: lit(13) },

      // ── Background ────────────────────────────────────────────────────
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'JustPulledCardPhoto',
    displayName: 'Just Pulled card — photo area',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['just-pulled'],
    // Radial edge-glow (transparent centre → tinted edges) behind the
    // pulled-card photo, Figma 682:3623 (common) / 682:3551 (rare). Rare
    // additionally gets an amber ring in the RarityBadge tone.
    knobs: {
      glowColor: {
        kind: 'color',
        label: 'Edge glow — colour (common)',
        default: lit('#FFFFFF'),
      },
      glowOpacity: {
        kind: 'opacity',
        label: 'Edge glow — strength (common)',
        default: lit(0.06),
      },
      rareGlowColor: {
        kind: 'color',
        label: 'Edge glow — colour (rare)',
        default: lit('#E99F1F'),
      },
      rareGlowOpacity: {
        kind: 'opacity',
        label: 'Edge glow — strength (rare)',
        default: lit(0.18),
      },
      bgFrom: {
        kind: 'color',
        label: 'Background — start (common)',
        default: lit('rgba(255,255,255,0)'),
      },
      bgTo: {
        kind: 'color',
        label: 'Background — end (common)',
        default: lit('rgba(255,255,255,0)'),
      },
      rareBgFrom: {
        kind: 'color',
        label: 'Background — start (rare)',
        default: lit('rgba(255,255,255,0)'),
      },
      rareBgTo: {
        kind: 'color',
        label: 'Background — end (rare)',
        default: lit('rgba(255,255,255,0)'),
      },
      // Optional photo layer between the edge glow (on top) and the base
      // gradient (below), `cover` + centred. `imageUrl` kind auto-wraps a
      // raw pasted URL with `url("…")`; `none` keeps the area image-free.
      // Short labels: the knob-row label column is ~21 chars wide, so the
      // distinguishing word must be visible without hovering the tooltip.
      bgImage: {
        kind: 'imageUrl',
        label: 'Bg image (common)',
        default: lit('none'),
      },
      rareBgImage: {
        kind: 'imageUrl',
        label: 'Bg image (rare)',
        default: lit('none'),
      },
      // Matches the RarityBadge sticker's default `from` tone so the badge
      // merges seamlessly with this ring.
      rareBorderColor: {
        kind: 'color',
        label: 'Border colour (rare)',
        default: lit('#E99F1F'),
      },
      rareBorderWidth: {
        kind: 'radius',
        label: 'Border width (px, rare, 0 = hidden)',
        default: lit(1.5),
      },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(25) },
    },
  },
  {
    name: 'JustPulledHeader',
    displayName: 'Just Pulled — header',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['just-pulled'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      // ── Title "Just Pulled" ───────────────────────────────────────────
      titleColor: { kind: 'color', label: 'Title — colour', default: lit('#FFFFFF') },
      titleFont: {
        kind: 'fontFamily',
        label: 'Title — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(18) },

      // ── Back button ──────────────────────────────────────────────────
      backButtonBg: {
        kind: 'color',
        label: 'Back button — fill',
        default: lit('rgba(255,255,255,0.20)'),
      },
      backButtonBorder: {
        kind: 'color',
        label: 'Back button — border',
        default: lit('rgba(171,174,179,0.24)'),
      },
      backButtonBorderWidth: {
        kind: 'radius',
        label: 'Back button — border width (px)',
        default: lit(1),
      },
      backIconColor: { kind: 'color', label: 'Back icon colour', default: lit('#FFFFFF') },

      // ── Header background ────────────────────────────────────────────
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(0,0,0,0)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(0,0,0,0)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = page behind visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(0),
      },
    },
  },
  {
    name: 'JustPulledPageBackground',
    displayName: 'Page background — Just Pulled page',
    group: 'Layout',
    templates: NOT_SLEEK,
    scenes: ['just-pulled'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top / outer colour (overrides global)',
        default: inherit('PageBackground', 'gradientFrom'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom / inner colour (overrides global)',
        default: inherit('PageBackground', 'gradientTo'),
      },
    },
  },
  {
    name: 'RevealPageBackground',
    displayName: 'Page background — Reveal page',
    group: 'Layout',
    scenes: ['reveal'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top colour (overrides global)',
        default: inherit('PageBackground', 'gradientFrom'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom colour (overrides global)',
        default: inherit('PageBackground', 'gradientTo'),
      },
    },
  },
  {
    name: 'MyPacksPageBackground',
    displayName: 'Page background — My Packs page',
    group: 'Layout',
    scenes: ['my-packs'],
    // My Packs intentionally does NOT inherit from the global PageBackground
    // / partner palette — the Figma design specifies a neutral dark grey
    // surface that should stay neutral regardless of the operator's primary
    // brand colour. Defaults are the literal Figma values; operators retint
    // by setting these knobs directly.
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top colour',
        default: lit('#181818'),
        templateDefaults: { sleek: lit('#0E0F11') },
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom colour',
        default: lit('#131313'),
        templateDefaults: { sleek: lit('#0E0F11') },
      },
      bgImage: {
        // Optional photo / illustration layer painted on top of the
        // grey gradient (`cover` + centred). Operator pastes a raw URL;
        // the `imageUrl` kind auto-wraps it with `url("…")`. `none`
        // keeps the page flat-grey.
        kind: 'imageUrl',
        label: 'Background image (paste URL or none)',
        default: lit('none'),
      },
      noiseOpacity: {
        // Visibility of the noise-SVG overlay painted on top of the
        // gradient. `1` = the default Figma noise, `0` = clean flat
        // gradient. Drives the `--co-MyPacksPageBackground-noiseOpacity`
        // var on `[data-page='my-packs']::before`. The
        // `hideBackgroundPattern` prop on MyPacksPage / DeliveryPageShell
        // inlines the same variable as `0` for screen-local overrides.
        kind: 'opacity',
        label: 'Noise overlay opacity (0 = off)',
        default: lit(1),
        templateDefaults: { sleek: lit(0) },
      },
    },
  },
  {
    // Per-page namespace for the Delivery New + Delivery Show shells.
    // Both Delivery pages share the same `<DeliveryPageShell>` and so
    // share one set of knobs — the operator builds them as a pair, not
    // each individually. Fully INDEPENDENT of MyPacks: the runtime CSS
    // never falls back to `--co-MyPacksPageBackground-*`, so a MyPacks
    // override doesn't leak onto Delivery. Defaults mirror the Figma
    // values (same as MyPacks) so an un-overridden Delivery looks like
    // the un-overridden MyPacks; setting MyPacks knobs only affects
    // MyPacks.
    name: 'DeliveryPageBackground',
    displayName: 'Page background — Delivery pages',
    group: 'Layout',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top colour',
        default: lit('#181818'),
        templateDefaults: { sleek: lit('#0E0F11') },
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom colour',
        default: lit('#131313'),
        templateDefaults: { sleek: lit('#0E0F11') },
      },
      bgImage: {
        kind: 'imageUrl',
        label: 'Background image (paste URL or none)',
        default: lit('none'),
      },
      noiseOpacity: {
        kind: 'opacity',
        label: 'Noise overlay opacity (0 = off)',
        default: lit(1),
        templateDefaults: { sleek: lit(0) },
      },
    },
  },
  {
    name: 'MyPacksHeader',
    displayName: 'My Packs — header bar',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
    },
  },
  {
    // Delivery New + Delivery Show header bar. Same component
    // (`DeliveryHeader`) renders on both screens; knobs here fall
    // through to `MyPacksHeader` via the CSS var cascade in
    // DeliveryHeader.tsx, so by default the Delivery header tracks
    // the MyPacks theme and the operator peels them off by setting
    // any slot.
    name: 'DeliveryHeader',
    displayName: 'Delivery — header bar',
    group: 'Misc',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: {
        kind: 'color',
        label: 'Title colour',
        default: lit('#FFFFFF'),
      },
      titleSize: {
        kind: 'fontSize',
        label: 'Title size (px)',
        default: lit(18),
      },
    },
  },
  {
    // Cross-cutting tab pill group. Rendered by the shared `<Tabs>`
    // design-system component so any tablist in the app picks up the
    // same theme tokens. Inspect via `data-co-component="Tabs"` on the
    // tablist `<div>`. All per-tab surface AND text knobs live here so
    // a single Design Constructor card themes every tablist at once.
    name: 'Tabs',
    displayName: 'Tab pill group (shared)',
    group: 'Panels',
    // The Custom Pack Lab has no tablist.
    excludeScenes: ['custom-pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'tabGroupBgDirection',
    variantLabel: 'Tab group background direction',
    knobs: {
      // Outer track — 5-knob gradient + border, same shape as
      // MyPacksItemCard so the operator's mental model stays consistent
      // across surfaces.
      tabGroupBgFrom: {
        kind: 'color',
        label: 'Tab group — background start',
        default: lit('rgba(255,255,255,0.20)'),
      },
      tabGroupBgTo: {
        kind: 'color',
        label: 'Tab group — background end',
        default: lit('rgba(153,153,153,0.20)'),
      },
      tabGroupBgDirection: {
        kind: 'text',
        label: 'Tab group — direction (use radio above)',
        default: lit('to bottom'),
      },
      tabGroupBgOpacity: {
        kind: 'opacity',
        label: 'Tab group — background opacity',
        default: lit(1),
      },
      tabGroupDarkTint: {
        kind: 'color',
        label: 'Tab group — dark tint base',
        default: lit('rgba(0,0,0,0.18)'),
      },
      tabGroupBorder: {
        kind: 'color',
        label: 'Tab group — border',
        default: lit('rgba(171,174,179,0.24)'),
      },
      // Active tab (inner pill) — same 5-knob gradient shape as the
      // outer track. `tabActiveBgDirection` is plain text (no radio)
      // because the schema supports a single `variantKnob` per
      // component and the outer track owns it.
      tabActiveBgFrom: {
        kind: 'color',
        label: 'Active tab — background start',
        default: lit('rgba(255,255,255,0.20)'),
      },
      tabActiveBgTo: {
        kind: 'color',
        label: 'Active tab — background end',
        default: lit('rgba(153,153,153,0.20)'),
      },
      tabActiveBgDirection: {
        kind: 'text',
        label: 'Active tab — direction (e.g. "to bottom")',
        default: lit('to bottom'),
      },
      tabActiveBgOpacity: {
        kind: 'opacity',
        label: 'Active tab — background opacity',
        default: lit(1),
      },
      tabActiveDarkTint: {
        kind: 'color',
        label: 'Active tab — dark tint base',
        default: lit('rgba(0,0,0,0.18)'),
      },
      tabActiveText: {
        kind: 'color',
        label: 'Active tab — text',
        default: lit('#FFFFFF'),
      },
      // Idle-tab text + tab font size. Live on `Tabs` (not on the
      // owning header) so a single sidebar card themes every tablist
      // in the app at once.
      tabIdleText: {
        kind: 'color',
        label: 'Idle tab — text',
        default: lit('#FFFFFF'),
      },
      tabIdleOpacity: {
        kind: 'opacity',
        label: 'Idle tab — opacity (0…1)',
        default: lit(0.5),
      },
      tabSize: { kind: 'fontSize', label: 'Tab — text size (px)', default: lit(15) },
    },
  },
  {
    // Wrapper container around the My Packs stats row + toolbar (Figma
    // 706:1854). Single registry entry that themes BOTH the bar surface
    // (background, border, radius) and the inner stats-row typography
    // (font family, value colour/size, separator dot). Each KPI pill
    // inside the row is its own per-card entry (`MyPacksStat*` below);
    // clicks between the pills still fall through to this bar.
    name: 'MyPacksStatsBar',
    displayName: 'My Packs — stats bar',
    group: 'Panels',
    scenes: ['my-packs'],
    knobs: {
      bandFill: { kind: 'color', label: 'Mobile band fill (sleek)', default: lit('#1A1B1F'), templates: ['sleek'] },
      // ── Wrapper surface — applied via inline `background` + container-
      //    query Tailwind classes. `fill` defaults to `transparent` so the
      //    un-overridden bar matches today's pre-Constructor look.
      //    `borderColor`/`borderWidth` only render at ≥600px (the mobile
      //    layout intentionally has no border per the Figma). Border
      //    radius reads ONE knob at both ≥600px (default 24) and ≥900px
      //    (default 40) so an un-overridden bar keeps the responsive
      //    bump; setting the knob collapses both breakpoints to one
      //    value (the operator's choice). ────────────────────────────────
      fill: { kind: 'color', label: 'Bar background', default: lit('transparent') },
      borderColor: {
        kind: 'color',
        label: 'Border colour (≥600px)',
        default: lit('rgba(242,242,242,0.10)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.01)') },
      },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, ≥600px)',
        default: lit(1),
      },
      borderRadius: {
        kind: 'radius',
        label: 'Border radius (px)',
        default: lit(24),
        templateDefaults: { sleek: lit(18) },
      },

      // ── Mobile-only stat pill fill (Figma 278:7388). The variable is
      //    read inside an `@max-[600px]:` arbitrary Tailwind class, so it
      //    ONLY paints when the container is below 600px — overriding it
      //    can't bleed onto the desktop inline-text layout. Default is a
      //    parseable solid colour (top stop of the Figma gradient) so the
      //    ColorField swatch + alpha slider stay enabled; the CSS-level
      //    fallback in MyPacksStatsRow.tsx still applies the original
      //    two-layer tint when no override is set. ─────────────────────
      statMobileFill: {
        kind: 'color',
        label: 'Stat fill (mobile only)',
        default: lit('rgba(255,255,255,0.20)'),
        templates: NOT_SLEEK,
      },

      // ── Stats row typography — formerly the standalone `MyPacksStatsRow`
      //    registry entry, folded into this bar so partners theme the whole
      //    block in one place. ────────────────────────────────────────────
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      valueColor: { kind: 'color', label: 'Value colour', default: lit('#FFFFFF'), templates: NOT_SLEEK },
      valueSize: { kind: 'fontSize', label: 'Value size (px)', default: lit(13), templates: NOT_SLEEK },
      separatorColor: {
        kind: 'color',
        label: 'Separator dot colour',
        default: lit('rgba(255,255,255,0.40)'),
        templates: NOT_SLEEK,
      },
    },
  },
  // ── My Packs KPI cards ─────────────────────────────────────────────
  // One registry entry PER stat card (client request: each themable on
  // its own). Identical knob sets built by the factory below. Surface
  // knobs (background / border / radius) only paint the <600px mobile
  // pill — the ≥600px layout is an inline text row with no surface.
  // Label / value colours apply at every width. Backgrounds fall back to
  // `MyPacksStatsBar.statMobileFill` so themes that already set the
  // shared fill keep painting all three cards alike.
  { ...myPacksStatCard('MyPacksStatPacksOpened', 'Packs Opened'), templates: NOT_SLEEK },
  myPacksStatCard('MyPacksStatEstimatedValue', 'Total Estimated Value'),
  { ...myPacksStatCard('MyPacksStatBuybackValue', 'Total Buyback Value'), templates: NOT_SLEEK },
  {
    name: 'MyPacksUnopenedPackCard',
    displayName: 'My Packs — unopened pack tile',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      bgImage: {
        // `imageUrl` kind auto-wraps a raw URL with `url("…")` at apply
        // time, so the operator can paste a plain URL directly from the
        // browser address bar. Default `none` keeps the tile flat-grey;
        // any URL or `linear-gradient(...)` overlays on top of the
        // inherited MyPacksItemCard glass background.
        kind: 'imageUrl',
        label: 'Background image (paste URL or none)',
        default: lit('none'),
      },
      // Background gradient — own knobs so the Packs tab can be themed
      // independently of Cards. The CSS-level fallback chain in
      // UnopenedPackCard reads MyPacksItemCard's value when a slot here
      // is unset, so leaving everything empty keeps both tabs in sync;
      // setting any of these peels Packs off into its own look.
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
      borderColor: {
        kind: 'color',
        label: 'Border colour',
        default: lit('rgba(171,174,179,0.24)'),
      },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px)',
        default: lit(2),
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      subtitleColor: { kind: 'color', label: 'Subtitle colour', default: lit('#EAEAEB') },
    },
  },
  {
    name: 'MyPacksItemCard',
    displayName: 'My Packs — card item',
    group: 'Panels',
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      valueColor: { kind: 'color', label: 'Card value colour (sleek)', default: lit('#FFFFFF'), templates: ['sleek'] },
      photoShadow: { kind: 'shadow', label: 'Photo shadow (sleek)', default: lit('0 4px 16px 2px rgba(0,0,0,0.2)'), templates: ['sleek'] },
      timerLineColor: { kind: 'color', label: 'Time-left line colour (sleek)', default: lit('#FFC933'), templates: ['sleek'] },
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF'), templateDefaults: { sleek: lit('#6D707C') } },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)'), templateDefaults: { sleek: lit('#1A1B1F') } },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)'), templates: NOT_SLEEK },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
        templates: NOT_SLEEK,
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
        templates: NOT_SLEEK,
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
        templates: NOT_SLEEK,
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)'), templateDefaults: { sleek: lit('rgba(255,255,255,0.01)') } },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
        templates: NOT_SLEEK,
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
        templates: NOT_SLEEK,
      },
      // Photo container — gradient + optional image overlay.
      //   • bgFrom / bgTo  — two-stop gradient (top → bottom). Default
      //     reuses the same colour in both stops to preserve the
      //     existing single-tint look; set different values for a
      //     glass gradient.
      //   • bgImage        — optional URL painted on top of the
      //     gradient (cover, centred). Operator pastes a raw URL; the
      //     `imageUrl` kind auto-wraps it with `url("…")`. `none`
      //     leaves the gradient flat.
      photoBgFrom: {
        kind: 'color',
        label: 'Photo bg top',
        default: lit('rgba(255,255,255,0.03)'),
        templates: NOT_SLEEK,
      },
      photoBgTo: {
        kind: 'color',
        label: 'Photo bg bottom',
        default: lit('rgba(255,255,255,0.03)'),
        templates: NOT_SLEEK,
      },
      photoBgImage: {
        kind: 'imageUrl',
        label: 'Photo image URL',
        default: lit('none'),
        templates: NOT_SLEEK,
      },
      timerBg: {
        kind: 'color',
        label: 'Timer chip — fill',
        default: lit('rgba(255,255,255,0.15)'),
        templates: NOT_SLEEK,
      },
      timerText: {
        kind: 'color',
        label: 'Timer chip — text',
        default: lit('#FFFFFF'),
        templates: NOT_SLEEK,
      },
      timerProgressTrack: {
        kind: 'color',
        label: 'Timer pie — elapsed track',
        default: lit('rgba(255,255,255,0.10)'),
        templates: NOT_SLEEK,
      },
      timerProgressUrgent: {
        kind: 'color',
        label: 'Timer pie — remaining (urgent)',
        default: lit('#DBB254'),
        templates: NOT_SLEEK,
      },
      timerProgressTrackUrgent: {
        kind: 'color',
        label: 'Timer pie — elapsed track (urgent)',
        default: lit('rgba(255,180,95,0.10)'),
        templates: NOT_SLEEK,
      },
      buybackText: {
        kind: 'color',
        label: 'Buyback button — text',
        default: lit('#FFFFFF'),
        templates: NOT_SLEEK,
      },
      // Selected-state visuals — used when the card is part of the My
      // Packs bulk-selection set (driven by `data-selected="true"`).
      // Defaults match the Buyback green so the highlight reads as
      // "this is what Buyback will sell"; operator can retint freely.
      selectedBorderColor: {
        kind: 'color',
        label: 'Selected — border colour',
        default: lit('#23D11D'),
        templates: NOT_SLEEK,
      },
      checkmarkBg: {
        kind: 'color',
        label: 'Selected — checkmark background',
        default: lit('#23D11D'),
        templates: NOT_SLEEK,
      },
      checkmarkColor: {
        kind: 'color',
        label: 'Selected — checkmark colour',
        default: lit('#FFFFFF'),
        templates: NOT_SLEEK,
      },
    },
  },
  {
    // Card surface used on the Deliveries tab — one tile per pool_shipping.
    // Mirrors the MyPacksItemCard knob set so partners get the same
    // expressiveness for the deliveries list as they have for the cards
    // list. Inspect via `data-co-component="MyPacksDeliveryCard"` on the
    // tile itself.
    name: 'MyPacksDeliveryCard',
    displayName: 'My Packs — delivery tile',
    group: 'Panels',
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      bg: { kind: 'color', label: 'Row background (sleek)', default: lit('#1A1B1F'), templates: ['sleek'] },
      dateColor: { kind: 'color', label: 'Date / chevron colour (sleek)', default: lit('#6D707C'), templates: ['sleek'] },
      photoShadow: { kind: 'shadow', label: 'Photo shadow (sleek)', default: lit('0 4px 16px 2px rgba(0,0,0,0.2)'), templates: ['sleek'] },
      photoPlaceholderFill: { kind: 'color', label: 'No-photo placeholder fill (sleek)', default: lit('rgba(255,255,255,0.04)'), templates: ['sleek'] },
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)'), templates: NOT_SLEEK },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)'), templates: NOT_SLEEK },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
        templates: NOT_SLEEK,
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
        templates: NOT_SLEEK,
      },
      glassBlur: { kind: 'radius', label: 'Glass blur (px, 0 = no blur)', default: lit(27), templates: NOT_SLEEK },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)'), templateDefaults: { sleek: lit('rgba(255,255,255,0.01)') } },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
        templates: NOT_SLEEK,
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
        templates: NOT_SLEEK,
      },
      // Photo container inside the delivery tile (preview area) —
      // mirrors the MyPacksItemCard.photoBg* set. Two-stop gradient
      // (top → bottom) + optional image overlay; default colours match
      // in both stops so an un-overridden surface renders as a solid
      // tint.
      photoBgFrom: {
        kind: 'color',
        label: 'Photo bg top',
        default: lit('rgba(255,255,255,0.03)'),
        templateDefaults: { sleek: lit('transparent') },
      },
      photoBgTo: {
        kind: 'color',
        label: 'Photo bg bottom',
        default: lit('rgba(255,255,255,0.03)'),
        templateDefaults: { sleek: lit('transparent') },
      },
      photoBgImage: {
        kind: 'imageUrl',
        label: 'Photo image URL',
        default: lit('none'),
      },
    },
  },
  {
    // Outer card surface used on Delivery New / Delivery Show pages —
    // the big rounded glass panel that wraps the form / detail content.
    // Same knob shape as MyPacksItemCard so partners can theme the
    // Delivery flow independently. Inspect via
    // `data-co-component="DeliveryFormCard"` on the wrapper.
    name: 'DeliveryFormCard',
    displayName: 'Delivery — form card',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: { kind: 'radius', label: 'Glass blur (px, 0 = no blur)', default: lit(27) },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.20)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
    },
  },
  {
    // Single tile in the shipping-list grid (Delivery New / Show). It used to
    // borrow DeliveryFormCard's background + MyPacksItemCard's border/blur; it
    // now owns a `--co-ShippingItemCard-*` namespace so it can be themed
    // separately. Defaults match the previous look exactly; the component CSS
    // still cascades each token to its old source, so an un-overridden card is
    // pixel-identical to before and keeps following those cards' themes.
    name: 'ShippingItemCard',
    displayName: 'Shipping list — card item',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: { kind: 'radius', label: 'Glass blur (px, 0 = no blur)', default: lit(27) },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.20)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(24) },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
      imageRadius: { kind: 'radius', label: 'Photo radius (px)', default: lit(22) },
    },
  },
  {
    name: 'MyPacksToolbarSubmit',
    displayName: 'My Packs — Submit button',
    group: 'Buttons',
    scenes: ['my-packs'],
    // Sleek's toolbar uses its own MyPacksShipButton / MyPacksSellButton.
    templates: ['classic', 'modern', 'glass'],
    // Literal Figma green — see MyPacksPageBackground for rationale on
    // skipping operator-palette inheritance on this page.
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#23D11D') },
    },
  },
  // Desktop-only "Select All" control in the My Packs stats bar (the
  // mobile Select All is a DS glass-frosted Button and themes through
  // the shared Button knobs). `checkedFill` inherits the Submit button
  // fill so un-touched themes keep the checkbox and the Buyback CTA in
  // the same green.
  {
    name: 'MyPacksSelectAll',
    displayName: 'My Packs — Select All',
    group: 'Buttons',
    scenes: ['my-packs'],
    // Sleek's toolbar uses its own MyPacksShipButton / MyPacksSellButton.
    templates: ['classic', 'modern', 'glass'],
    knobs: {
      textColor: { kind: 'color', label: 'Text colour', default: lit('#FFFFFF') },
      boxBorder: {
        kind: 'color',
        label: 'Checkbox border',
        default: lit('rgba(222,222,224,0.29)'),
      },
      checkedFill: {
        kind: 'color',
        label: 'Checkbox fill (checked)',
        default: inherit('MyPacksToolbarSubmit', 'fill'),
      },
      checkColor: { kind: 'color', label: 'Check icon colour', default: lit('#FFFFFF') },
      borderRadius: {
        kind: 'radius',
        label: 'Checkbox radius (px)',
        default: lit(7),
      },
    },
  },
  {
    name: 'MyPacksFooter',
    displayName: 'My Packs — footer links',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      linkColor: {
        kind: 'color',
        label: 'Link colour',
        default: lit('#DCDCDC'),
      },
      linkSize: { kind: 'fontSize', label: 'Link size (px)', default: lit(15), templateDefaults: { sleek: lit(13) } },
    },
  },
  {
    // Delivery New + Delivery Show footer links. Same MyPacksFooter
    // component renders on every page; knobs here fall through to
    // `MyPacksFooter` via the static cascade in MyPacksFooter.tsx,
    // so by default the Delivery footer tracks the MyPacks theme.
    name: 'DeliveryFooter',
    displayName: 'Delivery — footer links',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      linkColor: {
        kind: 'color',
        label: 'Link colour',
        default: lit('#DCDCDC'),
      },
      linkSize: {
        kind: 'fontSize',
        label: 'Link size (px)',
        default: lit(13),
      },
    },
  },
  {
    // "Delivery Shipping Options" section on the Delivery New page —
    // the section H3 + the empty-state card + the per-option radio
    // tiles (idle / active states). Backend-quoted prices and the
    // "Required" badge keep their existing namespaces.
    name: 'DeliveryShippingOptions',
    displayName: 'Delivery — Shipping Options',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      titleColor: { kind: 'color', label: 'Section title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Section title size (px)', default: lit(17) },
      // Each surface fill is now a two-stop gradient (top → bottom) so
      // operators can give the cards the same glass look as
      // DeliveryFormCard. Defaults reuse the same colour in both stops
      // to preserve the current single-tint look; pick different values
      // to enable the gradient.
      emptyStateBgFrom: {
        kind: 'color',
        label: 'Empty state — background top',
        default: lit('rgba(255,255,255,0.03)'),
      },
      emptyStateBgTo: {
        kind: 'color',
        label: 'Empty state — background bottom',
        default: lit('rgba(255,255,255,0.03)'),
      },
      emptyStateBorder: {
        kind: 'color',
        label: 'Empty state — border',
        default: lit('rgba(171,174,179,0.16)'),
      },
      emptyStateText: {
        kind: 'color',
        label: 'Empty state — text',
        default: lit('rgba(255,255,255,0.70)'),
      },
      optionIdleBgFrom: {
        kind: 'color',
        label: 'Option (idle) — background top',
        default: lit('rgba(255,255,255,0.04)'),
      },
      optionIdleBgTo: {
        kind: 'color',
        label: 'Option (idle) — background bottom',
        default: lit('rgba(255,255,255,0.04)'),
      },
      optionIdleBorder: {
        kind: 'color',
        label: 'Option (idle) — border',
        default: lit('rgba(255,255,255,0.08)'),
      },
      optionActiveBgFrom: {
        kind: 'color',
        label: 'Option (active) — background top',
        default: lit('rgba(35,209,29,0.10)'),
      },
      optionActiveBgTo: {
        kind: 'color',
        label: 'Option (active) — background bottom',
        default: lit('rgba(35,209,29,0.10)'),
      },
      optionActiveBorder: {
        kind: 'color',
        label: 'Option (active) — border',
        default: lit('#23D11D'),
      },
      optionText: {
        kind: 'color',
        label: 'Option — name colour',
        default: lit('#FFFFFF'),
      },
      optionDescText: {
        kind: 'color',
        label: 'Option — description colour',
        default: lit('rgba(255,255,255,0.55)'),
      },
      optionPriceText: {
        kind: 'color',
        label: 'Option — price colour',
        default: lit('#FFFFFF'),
      },
    },
  },
  {
    // "Your Address" section on the Delivery New page — section H3 +
    // empty-state card. When a saved address is shown the visual is
    // the shared `AddressBlock`, themed elsewhere.
    name: 'DeliveryYourAddress',
    displayName: 'Delivery — Your Address',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      titleColor: { kind: 'color', label: 'Section title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Section title size (px)', default: lit(17) },
      // Two-stop gradient (top → bottom) so the empty state can wear
      // the same glass look as DeliveryFormCard. Default reuses the
      // same colour in both stops → renders identically to the
      // previous single-tint fill.
      emptyStateBgFrom: {
        kind: 'color',
        label: 'Empty state — background top',
        default: lit('rgba(255,255,255,0.02)'),
      },
      emptyStateBgTo: {
        kind: 'color',
        label: 'Empty state — background bottom',
        default: lit('rgba(255,255,255,0.02)'),
      },
      emptyStateBorder: {
        kind: 'color',
        label: 'Empty state — border',
        default: lit('rgba(171,174,179,0.20)'),
      },
      emptyStatePromptText: {
        kind: 'color',
        label: 'Empty state — prompt colour',
        default: lit('#FFFFFF'),
      },
      emptyStateSubText: {
        kind: 'color',
        label: 'Empty state — subtext colour',
        default: lit('rgba(255,255,255,0.70)'),
      },
      // Selected-address card — AddressBlock rendered once the player
      // picks an address. AddressLines inside the card uses
      // `currentColor` + opacity, so `selectedText` cascades down to
      // the name + lines + phone (each line then dims itself via its
      // own opacity).
      selectedBgFrom: {
        kind: 'color',
        label: 'Selected address — background top',
        default: lit('rgba(255,255,255,0.02)'),
      },
      selectedBgTo: {
        kind: 'color',
        label: 'Selected address — background bottom',
        default: lit('rgba(255,255,255,0.02)'),
      },
      selectedBorder: {
        kind: 'color',
        label: 'Selected address — border',
        default: lit('rgba(171,174,179,0.20)'),
      },
      selectedText: {
        kind: 'color',
        label: 'Selected address — text',
        default: lit('#FFFFFF'),
      },
    },
  },
  {
    // "Select Items" picker (Figma — Delivery New item picker). Themes
    // the sheet surface, close chip, item tiles (idle / picked) and
    // their checkmark indicator. Backdrop is the shared `Modal` knob.
    name: 'ItemPickerModal',
    displayName: 'Select Items modal',
    group: 'Modals',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      sheetFill: { kind: 'color', label: 'Sheet — fill', default: lit('#15181E') },
      titleColor: { kind: 'color', label: 'Title — colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(20) },
      descColor: {
        kind: 'color',
        label: 'Description — colour',
        default: lit('rgba(255,255,255,0.55)'),
      },
      closeFill: { kind: 'color', label: 'Close (X) — fill', default: lit('rgba(255,255,255,0.08)') },
      closeIconColor: { kind: 'color', label: 'Close (X) — icon', default: lit('#FFFFFF') },
      tileIdleFill: {
        kind: 'color',
        label: 'Tile (idle) — fill',
        default: lit('rgba(255,255,255,0.03)'),
      },
      tileIdleBorder: {
        kind: 'color',
        label: 'Tile (idle) — border',
        default: lit('rgba(255,255,255,0.08)'),
      },
      tilePickedFill: {
        kind: 'color',
        label: 'Tile (picked) — fill',
        default: lit('rgba(35,209,29,0.08)'),
      },
      tilePickedBorder: {
        kind: 'color',
        label: 'Tile (picked) — border',
        default: lit('#23D11D'),
      },
      tileText: { kind: 'color', label: 'Tile — title colour', default: lit('#FFFFFF') },
      // Added later — safe to append: missing overrides fall back to these
      // defaults, so themes saved before this knob existed are unchanged.
      tileTextSize: { kind: 'fontSize', label: 'Tile — title size (px)', default: lit(12) },
      tileImgBg: {
        kind: 'color',
        label: 'Tile — image container',
        default: lit('rgba(255,255,255,0.04)'),
      },
      tileRadius: { kind: 'radius', label: 'Tile — corner radius (px)', default: lit(16) },
      tileImgRadius: { kind: 'radius', label: 'Tile — image radius (px)', default: lit(12) },
      tileBorderWidth: { kind: 'radius', label: 'Tile — border width (px)', default: lit(1.5) },
      checkmarkBg: {
        kind: 'color',
        label: 'Checkmark — background',
        default: lit('#23D11D'),
      },
      checkmarkColor: {
        kind: 'color',
        label: 'Checkmark — colour',
        default: lit('#0A0A0A'),
      },
      // Added later — idle (unselected) checkmark; older themes that never
      // set these keep the original hard-coded look via the defaults.
      checkmarkIdleBg: {
        kind: 'color',
        label: 'Checkmark (idle) — background',
        default: lit('rgba(0,0,0,0.35)'),
      },
      checkmarkIdleBorder: {
        kind: 'color',
        label: 'Checkmark (idle) — border',
        default: lit('rgba(255,255,255,0.5)'),
      },
      emptyText: {
        kind: 'color',
        label: 'Empty list — text',
        default: lit('rgba(255,255,255,0.55)'),
      },
    },
  },
  {
    name: 'MyPacksEmptyState',
    displayName: 'My Packs — empty state',
    group: 'Panels',
    scenes: ['my-packs'],
    knobs: {
      // Sleek's empty state has no illustration (partner Figma 2026-10-14).
      iconBgFrom: {
        kind: 'color',
        label: 'Icon disc — top colour',
        default: lit('rgba(255,255,255,0.10)'),
        templates: NOT_SLEEK,
      },
      iconBgTo: {
        kind: 'color',
        label: 'Icon disc — bottom colour',
        default: lit('rgba(255,255,255,0.04)'),
        templates: NOT_SLEEK,
      },
      iconBorder: {
        kind: 'color',
        label: 'Icon disc — border',
        default: lit('rgba(255,255,255,0.10)'),
        templates: NOT_SLEEK,
      },
      iconColor: {
        kind: 'color',
        label: 'Icon colour',
        default: lit('rgba(255,255,255,0.55)'),
        templates: NOT_SLEEK,
      },
      titleColor: {
        kind: 'color',
        label: 'Title colour',
        default: lit('#FFFFFF'),
        templateDefaults: { sleek: lit('#F4F4F5') },
      },
      titleSize: {
        kind: 'fontSize',
        label: 'Title size (px)',
        default: lit(18),
        templateDefaults: { sleek: lit(17) },
      },
      descriptionColor: {
        kind: 'color',
        label: 'Description colour',
        default: lit('rgba(255,255,255,0.60)'),
        templateDefaults: { sleek: lit('#6D717E') },
      },
    },
  },
  {
    // Loading-skeleton tile rendered in the three My Packs tabs (Repackz,
    // Cards, Deliveries) while the first/next page is in flight. Inspect
    // via `data-co-component="MyPacksSkeleton"`. Shape (radius / aspect)
    // is owned by the consuming tab so each skeleton matches the card it
    // stands in for; only the surface knobs live here.
    name: 'MyPacksSkeleton',
    displayName: 'My Packs — loading skeleton',
    group: 'Panels',
    scenes: ['my-packs'],
    knobs: {
      fill: {
        kind: 'color',
        label: 'Skeleton fill',
        default: lit('rgba(255,255,255,0.05)'),
      },
      borderColor: {
        kind: 'color',
        label: 'Border colour',
        default: lit('rgba(171,174,179,0.24)'),
      },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
      },
    },
  },
  {
    // Shared modal frame — backdrop tint + blur. Cross-cutting (no
    // `scenes`) so a single change applies to every modal in the app.
    // Per-modal content (titles, cards, buttons) keeps its OWN
    // component entry below — those knobs theme only that modal's
    // body, not the wrapper.
    name: 'Modal',
    displayName: 'Modal (shared frame)',
    group: 'Modals',
    knobs: {
      backdropFill: {
        kind: 'color',
        label: 'Backdrop tint',
        default: lit('rgba(0,0,0,0.45)'),
      },
      backdropBlur: {
        kind: 'radius',
        label: 'Backdrop blur (px)',
        default: lit(20),
      },
    },
  },
  // Reusable notification "pill" (Figma 1251:1131) — the generic
  // namespace every Notification consumer falls back to when no
  // per-instance knob is set. Buyback / future named instances live
  // under their own `<Name>Notification` entries (see below) and the
  // component chains `var(--co-<Name>-<slot>, var(--co-Notification-<slot>, …))`.
  {
    name: 'Notification',
    displayName: 'Notification pill',
    group: 'Misc',
    // `pack`: a reveal that ends drops the player on the pack page, where
    // its toast is still up. `custom-pack`: a sell or an open that fails in
    // the lab toasts as well.
    scenes: ['pack', 'reveal', 'my-packs', 'custom-pack'],
    knobs: {
      // Moves every toast — they all share one sonner list (App.tsx).
      offsetTop: {
        kind: 'radius',
        label: 'Top offset (px)',
        default: lit(16),
        hint: 'Distance from the top edge. Unset: 16px on phones, 24px on wider screens.',
      },
      // Two-stop vertical gradient (top → bottom) matches the visual
      // pattern used by JustPulled / OddsBreakdown / etc. — each stop
      // is a separate parseable colour so the ColorField swatch + alpha
      // slider stay enabled.
      bgFrom: {
        kind: 'color',
        label: 'Background — start',
        default: lit('#15181E'),
      },
      bgTo: {
        kind: 'color',
        label: 'Background — end',
        default: lit('#15181E'),
      },
      border: {
        kind: 'color',
        label: 'Border',
        default: lit('rgba(255,255,255,0.08)'),
      },
      shadow: {
        kind: 'shadow',
        label: 'Shadow',
        default: lit('0px 24px 24px 0px rgba(0,0,0,0.25)'),
      },
      text: {
        kind: 'color',
        label: 'Text colour',
        default: lit('#FFFFFF'),
      },
      accent: {
        kind: 'color',
        label: 'Accent (amount)',
        default: lit('#54E24F'),
      },
    },
  },
  // Per-instance Buyback notification namespace. Knobs mirror the
  // generic `Notification` slots; the component reads
  // `var(--co-BuybackNotification-<slot>, var(--co-Notification-<slot>, …))`
  // so this entry wins when set and otherwise falls through to the
  // shared Notification theme above. `accent` is intentionally omitted —
  // the buyback toast doesn't render the highlight span yet (see
  // `notifications/buyback.tsx`), so the slot would be a dead knob.
  {
    name: 'BuybackNotification',
    displayName: 'Buyback notification',
    group: 'Misc',
    scenes: ['pack', 'reveal', 'my-packs'],
    knobs: {
      // Read by modules/notifications through `--ui-notification-buyback`.
      visibility: {
        kind: 'text',
        label: 'Show',
        default: lit('shown'),
        options: NOTIFICATION_VISIBILITY,
        hint: 'The toast after a card is sold.',
      },
      // Icon-only knob (no generic Notification counterpart — every
      // notification instance brings its own icon art). Unset keeps the
      // bundled coins image; the `imageUrl` kind auto-wraps a pasted raw
      // URL with `url("…")` at apply time.
      iconImage: {
        kind: 'imageUrl',
        label: 'Icon image URL (empty = default coins)',
        default: lit('none'),
      },
      iconRadius: {
        kind: 'radius',
        label: 'Icon border radius (px)',
        default: lit(16),
        templateDefaults: { sleek: lit(0) },
      },
      bgFrom: {
        kind: 'color',
        label: 'Background — start',
        default: inherit('Notification', 'bgFrom'),
      },
      bgTo: {
        kind: 'color',
        label: 'Background — end',
        default: inherit('Notification', 'bgTo'),
      },
      border: {
        kind: 'color',
        label: 'Border',
        default: inherit('Notification', 'border'),
      },
      shadow: {
        kind: 'shadow',
        label: 'Shadow',
        default: inherit('Notification', 'shadow'),
      },
      text: {
        kind: 'color',
        label: 'Text colour',
        default: inherit('Notification', 'text'),
      },
    },
  },
  // Sleek's "N cards have been added to your vault" toast. Only on/off
  // switches: its look comes from the shared Notification knobs above.
  {
    name: 'VaultedNotification',
    displayName: 'Vault notification',
    group: 'Misc',
    scenes: ['pack', 'reveal'],
    templates: ['sleek'],
    knobs: {
      visibility: {
        kind: 'text',
        label: 'Show after Vault',
        default: lit('shown'),
        options: NOTIFICATION_VISIBILITY,
        hint: 'The toast after the player keeps a card with Vault.',
      },
      exitVisibility: {
        kind: 'text',
        label: 'Show after leaving',
        default: lit('shown'),
        options: NOTIFICATION_VISIBILITY,
        hint: 'The toast after "Close and vault" — leaving a reveal with packs still sealed.',
      },
    },
  },

  // ─── Shipping modal ─────────────────────────────────────────
  // ONE registry entry for the single reusable ShippingModal (Figma
  // 703:28734), shown in BOTH the reveal "Create Shipping" flow and
  // the My Packs ship disclaimer. Every section (countdown, title /
  // description, fees breakdown, note) is themed under the one
  // `--co-ShippingModal-*` namespace — right-click anywhere on the
  // sheet opens this single DC slot.
  //
  // Backdrop is NOT a per-modal knob — the dialog reads
  // `--co-Modal-backdropFill` straight from the shared DS Modal.
  //
  // Buttons (Buyback / Confirm Shipping / Add Delivery) stay on the
  // global DS Button knobs (`primary` + `glass-frosted`) so they theme
  // alongside every other CTA in the app — no per-modal button knobs.
  {
    name: 'ShippingModal',
    displayName: 'Shipping modal',
    group: 'Modals',
    templates: NOT_SLEEK,
    scenes: ['reveal', 'my-packs'],
    knobs: {
      sheetRadius: { kind: 'radius', label: 'Sheet top radius (px, sleek)', default: lit(24), templates: ['sleek'] },
      handle: { kind: 'color', label: 'Drag handle (sleek)', default: lit('#2F3037'), templates: ['sleek'] },
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#FFFFFF'),
        templateDefaults: { sleek: lit('#0E0F11') },
      },
      // Countdown header (reveal flow)
      countdownLabelFont: {
        kind: 'fontFamily',
        label: 'Countdown — caption font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      countdownLabelSize: {
        kind: 'fontSize',
        label: 'Countdown — caption size (px)',
        default: lit(15),
      },
      countdownLabelColor: {
        kind: 'color',
        label: 'Countdown — caption colour',
        default: lit('rgba(0,0,0,0.85)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.6)') },
      },
      chipFont: {
        kind: 'fontFamily',
        label: 'Countdown chip — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      chipSize: {
        kind: 'fontSize',
        label: 'Countdown chip — size (px)',
        default: lit(15),
      },
      chipFill: {
        kind: 'color',
        label: 'Countdown chip — fill',
        default: lit('rgba(0,0,0,0.07)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.08)') },
      },
      chipText: {
        kind: 'color',
        label: 'Countdown chip — text',
        default: lit('rgba(0,0,0,0.85)'),
        templateDefaults: { sleek: lit('#FFFFFF') },
      },
      // Title + lead description (disclaimer flow)
      titleColor: {
        kind: 'color',
        label: 'Title — colour',
        default: lit('#15181E'),
        templateDefaults: { sleek: lit('#FFFFFF') },
      },
      titleSize: {
        kind: 'fontSize',
        label: 'Title — size (px)',
        default: lit(17),
        templates: NOT_SLEEK,
      },
      descFont: {
        kind: 'fontFamily',
        label: 'Description — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      descSize: {
        kind: 'fontSize',
        label: 'Description — size (px)',
        default: lit(16),
        templates: NOT_SLEEK,
      },
      descColor: {
        kind: 'color',
        label: 'Description — colour',
        default: lit('rgba(0,0,0,0.75)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.6)') },
      },
      // Fees breakdown (positions)
      feeCardFill: {
        kind: 'color',
        label: 'Fees card — fill',
        default: lit('#FFFFFF'),
        templateDefaults: { sleek: lit('transparent') },
      },
      feeCardBorder: {
        kind: 'color',
        label: 'Fees card — border',
        default: lit('rgba(0,0,0,0.08)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.08)') },
      },
      rowFont: {
        kind: 'fontFamily',
        label: 'Fee row — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      rowLabelSize: {
        kind: 'fontSize',
        label: 'Fee row — label size (px)',
        default: lit(17),
      },
      rowLabelColor: {
        kind: 'color',
        label: 'Fee row — label colour',
        default: lit('rgba(0,0,0,0.55)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.5)') },
      },
      rowValueSize: {
        kind: 'fontSize',
        label: 'Fee row — value size (px)',
        default: lit(17),
      },
      rowValueColor: {
        kind: 'color',
        label: 'Fee row — value colour',
        default: lit('#15181E'),
        templateDefaults: { sleek: lit('#FFFFFF') },
      },
      rowDivider: {
        kind: 'color',
        label: 'Fee row — divider colour',
        default: lit('rgba(0,0,0,0.06)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.08)') },
      },
      // Note / fine print (below the breakdown)
      noteFont: {
        kind: 'fontFamily',
        label: 'Note — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      noteSize: {
        kind: 'fontSize',
        label: 'Note — size (px)',
        default: lit(16),
        templates: NOT_SLEEK,
      },
      noteColor: {
        kind: 'color',
        label: 'Note — colour',
        default: lit('rgba(0,0,0,0.75)'),
        templateDefaults: { sleek: lit('rgba(255,255,255,0.6)') },
      },
      // Close (X) chip
      closeFill: {
        kind: 'color',
        label: 'Close (X) — fill',
        default: lit('rgba(0,0,0,0.6)'),
        templates: NOT_SLEEK,
      },
      closeIconColor: {
        kind: 'color',
        label: 'Close (X) — icon colour',
        default: lit('#FFFFFF'),
        templates: NOT_SLEEK,
      },
    },
  },
  {
    name: 'AddressModal',
    displayName: 'Address form — modal',
    group: 'Modals',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      cardFill: { kind: 'color', label: 'Card — fill', default: lit('#FFFFFF') },
      textColor: { kind: 'color', label: 'Title / text — colour', default: lit('#15181E') },
      linkColor: {
        kind: 'color',
        label: '"Enter manually" link — colour',
        default: lit('rgba(21,24,30,0.6)'),
      },
      closeFill: {
        kind: 'color',
        label: 'Close (X) — fill',
        default: lit('rgba(0,0,0,0.05)'),
      },
      closeIconColor: {
        kind: 'color',
        label: 'Close (X) — icon colour',
        default: lit('#15181E'),
      },
    },
  },
  {
    name: 'AddressPickerModal',
    displayName: 'Address picker — modal',
    group: 'Modals',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      cardFill: { kind: 'color', label: 'Card — fill', default: lit('#FFFFFF') },
      textColor: { kind: 'color', label: 'Title / text — colour', default: lit('#15181E') },
      closeFill: { kind: 'color', label: 'Close (X) — fill', default: lit('rgba(0,0,0,0.05)') },
      closeIconColor: { kind: 'color', label: 'Close (X) — icon colour', default: lit('#15181E') },
      rowText: { kind: 'color', label: 'Address row — text colour', default: lit('#15181E') },
      rowFill: { kind: 'color', label: 'Address row — fill', default: lit('rgba(0,0,0,0.02)') },
      rowBorder: { kind: 'color', label: 'Address row — border', default: lit('rgba(0,0,0,0.10)') },
      rowSelectedFill: {
        kind: 'color',
        label: 'Address row (selected) — fill',
        default: lit('rgba(34,225,28,0.07)'),
      },
      rowSelectedBorder: {
        kind: 'color',
        label: 'Address row (selected) — border / check',
        default: lit('#16B814'),
      },
      radioBorder: {
        kind: 'color',
        label: 'Radio (unselected) — border',
        default: lit('rgba(0,0,0,0.25)'),
      },
    },
  },
  {
    name: 'CancelDeliveryModal',
    displayName: 'Cancel delivery — confirm modal',
    group: 'Modals',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      cardFill: { kind: 'color', label: 'Card — fill', default: lit('#FFFFFF') },
      textColor: { kind: 'color', label: 'Title / text — colour', default: lit('#15181E') },
      descColor: {
        kind: 'color',
        label: 'Description — colour',
        default: lit('rgba(0,0,0,0.75)'),
      },
      errorColor: { kind: 'color', label: 'Error — colour', default: lit('#E5484D') },
    },
  },
  {
    name: 'CancelDeliveryButton',
    displayName: 'Cancel delivery — button',
    group: 'Buttons',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      textColor: { kind: 'color', label: 'Text', default: lit('#F87171') },
    },
  },
  {
    name: 'TextField',
    displayName: 'Text input (DS)',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Input — fill', default: lit('#F2F2F2') },
      border: { kind: 'color', label: 'Input — border', default: lit('#E2E2E5') },
      textColor: { kind: 'color', label: 'Input — text colour', default: lit('#15181E') },
      labelColor: { kind: 'color', label: 'Label — colour', default: lit('#6B7280') },
      focusRing: {
        kind: 'color',
        label: 'Focus ring — colour',
        default: lit('rgba(34,225,28,0.35)'),
      },
    },
  },
  {
    name: 'DefaultBadge',
    displayName: 'Default-address badge',
    group: 'Misc',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      bg: { kind: 'color', label: 'Badge — fill', default: lit('rgba(34,225,28,0.16)') },
      fg: { kind: 'color', label: 'Badge — text colour', default: lit('#16B814') },
    },
  },

  // ─── Winner screen elements ─────────────────────────────────
  {
    name: 'BadgeRevealSequence',
    displayName: 'Badge reveal — character/grade text',
    group: 'Reveal',
    // `custom-pack`: the lab's own reveal runs the same sequence.
    scenes: ['reveal', 'custom-pack'],
    section: 'Reveal',
    knobs: {
      color: {
        kind: 'color',
        label: 'Text colour',
        default: lit('#15181E'),
      },
    },
  },
  {
    // The light of a reveal: the rotating rays round the pack and the
    // blurred plate that swells behind the card (RevealRays.tsx,
    // RevealGlowOverlay.tsx). Both white until set, on every template and
    // in the Custom Pack Lab's own reveal.
    name: 'RevealLight',
    displayName: 'Reveal — light rays and glow plate',
    description: 'The rotating rays round the pack while it opens, and the blurred plate that swells behind the card.',
    group: 'Reveal',
    scenes: ['reveal', 'custom-pack'],
    section: 'Reveal',
    knobs: {
      rays: {
        kind: 'color',
        label: 'Rays',
        default: lit('rgba(255,255,255,0.20)'),
        hint: 'The bright stripes; the gaps between them stay transparent.',
      },
      plate: {
        kind: 'color',
        label: 'Glow plate',
        default: lit('#FFFFFF'),
        hint: 'Blurred behind the card, then swollen to fill the screen.',
      },
    },
  },
  {
    name: 'WinnerStepTitle',
    displayName: 'Winner screen — title',
    group: 'Reveal',
    scenes: ['reveal'],
    templateScenes: { sleek: ['reveal', 'my-packs'] },
    knobs: {
      font: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      // No `size` knob — the title auto-fits its font size to `maxLines`
      // (see WinnerContent's useFitText), so a fixed override would fight it.
      color: {
        kind: 'color',
        label: 'Text colour',
        default: lit('#FFFFFF'),
      },
      maxLines: {
        kind: 'number',
        label: 'Title — max lines',
        hint: 'Long card names shrink to fit this many lines, then clip with an ellipsis.',
        default: lit(3),
        templates: NOT_SLEEK,
      },
      exitSpacerWidth: {
        kind: 'radius',
        label: 'Exit button spacer width (px)',
        default: lit(88),
        templates: NOT_SLEEK,
      },
      exitSpacerHeight: {
        kind: 'radius',
        label: 'Exit button spacer height (px)',
        default: lit(52),
        templates: NOT_SLEEK,
      },
    },
  },
  {
    name: 'WinnerStepFMV',
    displayName: 'Winner screen — Fair Market Value line',
    group: 'Reveal',
    scenes: ['reveal'],
    templateScenes: { sleek: ['reveal', 'my-packs'] },
    knobs: {
      font: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: {
        kind: 'fontSize',
        label: 'Font size (px)',
        default: lit(14),
      },
      color: {
        kind: 'color',
        label: 'Text colour',
        default: lit('rgba(255,255,255,0.60)'),
        templateDefaults: { sleek: lit('#6D707C') },
      },
    },
  },
  {
    name: 'WinnerStepProfit',
    displayName: 'Winner screen — PROFIT line',
    group: 'Reveal',
    scenes: ['reveal'],
    templateScenes: { sleek: ['reveal', 'my-packs'] },
    knobs: {
      font: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: {
        kind: 'fontSize',
        label: 'Font size (px)',
        default: lit(44),
      },
      labelColor: {
        kind: 'color',
        label: 'Label "PROFIT" colour',
        default: lit('#FFFFFF'),
      },
      valueColor: {
        kind: 'color',
        label: 'Amount "+$X.XX" colour',
        default: lit('#23D11D'),
        templateDefaults: { sleek: lit('#16A34A') },
      },
    },
  },
  {
    name: 'ReelErrorMessage',
    displayName: 'Open Next Pack — error panel',
    group: 'Reveal',
    scenes: ['reveal'],
    knobs: {
      iconBg: {
        kind: 'color',
        label: 'Alert icon background',
        default: lit('rgba(239,68,68,0.15)'),
      },
      iconColor: {
        kind: 'color',
        label: 'Alert icon colour',
        default: lit('#EF4444'),
      },
    },
  },
  // "Create Shipping" footer link uses the DS `Button → ghost`
  // variant — no dedicated `WinnerStepShipping` component here.
  // Theming flows through the shared `Button → ghost` knobs.
  {
    name: 'PotentialHits',
    displayName: 'Potential Hits panel',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      labelColor: { kind: 'color', label: 'Label colour ("POTENTIAL HITS")', default: lit('#FFFFFF') },
      labelSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(18) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'PotentialHitItem',
    displayName: 'Potential Hit card',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    knobs: {
      // Default is a parseable solid colour so the ColorField stays
      // enabled; the component falls back to the original light gradient
      // (CARD_BG) at the CSS level when no override is set.
      background: { kind: 'color', label: 'Background colour', default: lit('#F7F8F8') },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('#EAEAEB') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(20) },
    },
  },
  {
    name: 'JustPulled',
    displayName: 'Just Pulled feed',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      labelColor: { kind: 'color', label: 'Label colour ("JUST PULLED")', default: lit('#FFFFFF') },
      labelSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(18) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      chevronBg: { kind: 'color', label: 'Chevron background', default: lit('rgba(255,255,255,0.20)') },
      chevronIcon: { kind: 'color', label: 'Chevron icon colour', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
      // ── Chip (single pulled-card item inside the strip) ──────────
      // Rare and common chips get independent borders. Defaults match the
      // original look: common chips borderless (width 0), rare chips a 1px
      // white highlight. Radius rounds every chip's corners.
      chipBgFrom: {
        kind: 'color',
        label: 'Chip background — start (common)',
        default: lit('rgba(255,255,255,0.20)'),
      },
      chipBgTo: {
        kind: 'color',
        label: 'Chip background — end (common)',
        default: lit('rgba(153,153,153,0.20)'),
      },
      chipRareBgFrom: {
        kind: 'color',
        label: 'Chip background — start (rare)',
        default: lit('rgba(255,255,255,0.20)'),
      },
      chipRareBgTo: {
        kind: 'color',
        label: 'Chip background — end (rare)',
        default: lit('rgba(153,153,153,0.20)'),
      },
      chipBorderColor: {
        kind: 'color',
        label: 'Chip border colour (common)',
        default: lit('#FFFFFF'),
      },
      chipBorderWidth: {
        kind: 'radius',
        label: 'Chip border width (px, common, 0 = hidden)',
        default: lit(0),
      },
      chipRareBorderColor: {
        kind: 'color',
        label: 'Chip border colour (rare)',
        default: lit('#FFFFFF'),
      },
      chipRareBorderWidth: {
        kind: 'radius',
        label: 'Chip border width (px, rare, 0 = hidden)',
        default: lit(1),
      },
      chipBorderRadius: { kind: 'radius', label: 'Chip border radius (px)', default: lit(12) },
      // Inline "RARE" gradient text on rare chips (RarityBadge `text`
      // variant) — lives here rather than in the RarityBadge section
      // because the chips are the only place the text variant renders.
      chipRareTextGradientFrom: {
        kind: 'color',
        label: 'Chip rare text — gradient start',
        default: lit('#E99F1F'),
      },
      chipRareTextGradientTo: {
        kind: 'color',
        label: 'Chip rare text — gradient end',
        default: lit('#FFFFFF'),
      },
    },
  },
  {
    name: 'OddsCard',
    displayName: 'Odds card (classic)',
    group: 'Panels',
    scenes: ['pack'],
    templates: ['classic'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      // ── ODDS heading ─────────────────────────────────────────────
      titleColor: { kind: 'color', label: 'Title — colour ("ODDS")', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(18) },
      // ── Section labels (Big win chance, Expected Value, Common, Uncommon, % Chance) ─
      labelColor: { kind: 'color', label: 'Label — colour', default: lit('rgba(255,255,255,0.5)') },
      labelSize: { kind: 'fontSize', label: 'Label — size (px)', default: lit(13) },
      // ── Stat values (33.34%, $5 - $15) ──────────────────────────
      valueColor: { kind: 'color', label: 'Value — colour', default: lit('#FFFFFF') },
      valueSize: { kind: 'fontSize', label: 'Value — size (px)', default: lit(18) },
      // ── Background ──────────────────────────────────────────────
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'StatsRow',
    displayName: 'Pack stats row',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      valueColor: { kind: 'color', label: 'Value colour ($, %, …)', default: lit('#FFFFFF') },
      valueSize: { kind: 'fontSize', label: 'Value size (px)', default: lit(20), templateDefaults: { sleek: lit(16) } },
      labelColor: {
        kind: 'color',
        label: 'Label colour (Min Value / Big Win Chance)',
        default: lit('rgba(255,255,255,0.5)'),
        templateDefaults: { sleek: lit('#6D707C') },
      },
      labelSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(13), templateDefaults: { sleek: lit(12) } },
      divider: { kind: 'color', label: 'Lines beside "Max Pull" (sleek)', default: lit('rgba(255,255,255,0.03)') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)'), templateDefaults: { sleek: lit('#1A1B1F') } },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)'), templateDefaults: { sleek: lit('#1A1B1F') } },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
        templateDefaults: { sleek: lit(0) },
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(0),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px, desktop ≥900px)', default: lit(28) },
    },
  },
  {
    // Odds breakdown surface — the per-rarity panel in the odds modal (Figma
    // 1334:85344, every width). Deliberately separate from `StatsRow` (which
    // themes the Max Pull block + its in-modal recap) so operators style the
    // breakdown card, rarity bars and "Mod:" caption independently.
    name: 'OddsBreakdown',
    displayName: 'Odds breakdown (modal)',
    group: 'Panels',
    scenes: ['pack'],
    // Sleek replaced it with the OddsSheet picker on both widths.
    templates: ['modern', 'glass'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      bgFrom: { kind: 'color', label: 'Card background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Card background — end', default: lit('rgba(153,153,153,0.20)') },
      borderColor: { kind: 'color', label: 'Card border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: { kind: 'radius', label: 'Card border width (px, 0 = hidden)', default: lit(2) },
      borderRadius: { kind: 'radius', label: 'Card border radius (px)', default: lit(28) },
      barTrack: { kind: 'color', label: 'Bar track', default: lit('rgba(255,255,255,0.05)') },
      barNameColor: { kind: 'color', label: 'Bar — rarity name', default: lit('#FFFFFF') },
      barRangeColor: { kind: 'color', label: 'Bar — value range', default: lit('rgba(255,255,255,0.5)') },
      barPercentColor: { kind: 'color', label: 'Bar — percentage', default: lit('#FFFFFF') },
      barRadius: { kind: 'radius', label: 'Bar corner radius (px)', default: lit(16) },
      barFillRadius: { kind: 'radius', label: 'Bar fill corner radius (px)', default: lit(6) },
      modLabelColor: { kind: 'color', label: '"Mod:" label colour', default: lit('rgba(255,255,255,0.5)') },
      modValueColor: { kind: 'color', label: 'Mod value colour', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'SliderDots',
    displayName: 'Carousel dots',
    group: 'Misc',
    scenes: ['pack'],
    knobs: {
      active: { kind: 'color', label: 'Active dot', default: lit('rgba(255,255,255,1)') },
      idle: { kind: 'color', label: 'Idle dot', default: lit('rgba(255,255,255,0.4)') },
    },
  },
  {
    // Sleek and glass are drawn without a pedestal (partner mockup, Repackz
    // design); an operator can still put one under the lobby pack. Read by
    // their Sliders through `--ui-pedestal-lobby` in index.css.
    name: 'Pedestal',
    displayName: 'Pedestal',
    group: 'Misc',
    scenes: ['pack'],
    templates: ['sleek', 'glass'],
    knobs: {
      lobby: {
        kind: 'text',
        label: 'Show in the lobby',
        default: lit('hidden'),
        options: NOTIFICATION_VISIBILITY,
        hint: 'The plinth under the pack. Uses the pedestal image set on the pack, if any.',
      },
    },
  },
  {
    // For a host that answers a short balance with its own top-up popup
    // (Ambika, 2026-11-02). Read through `--ui-low-balance` in index.css.
    name: 'LowBalance',
    displayName: 'Low balance message',
    group: 'Modals',
    scenes: ['pack'],
    knobs: {
      visibility: {
        kind: 'text',
        label: 'Show',
        default: lit('shown'),
        options: NOTIFICATION_VISIBILITY,
        hint: 'The "Insufficient balance" dialog on a purchase and the low balance error on a delivery. Hide them when your site opens its own top-up on the purchase_failed / shipping_failed events.',
      },
    },
  },
  {
    // Mobile pack title — the text shown under the pedestal when the pack has
    // no logo image. Drives `--co-PackTitle-*`, consumed by the modern Slider.
    name: 'PackTitle',
    displayName: 'Pack title (mobile)',
    group: 'Misc',
    scenes: ['pack'],
    templates: ['modern', 'sleek', 'glass'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: { kind: 'fontSize', label: 'Font size (px)', default: lit(24) },
      weight: { kind: 'fontWeight', label: 'Font weight', default: lit(700), templateDefaults: { sleek: lit(600) } },
      color: { kind: 'color', label: 'Text colour', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'BottomActionShell',
    displayName: 'Buy button — shell',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'gradientDirection',
    variantLabel: 'Gradient direction',
    knobs: {
      gradientFrom: { kind: 'color', label: 'Shell colour — start', default: lit('#FFFFFF'), templateDefaults: { sleek: lit('transparent') } },
      gradientTo: { kind: 'color', label: 'Shell colour — end', default: lit('#FFFFFF'), templateDefaults: { sleek: lit('transparent') } },
      gradientDirection: {
        kind: 'text',
        label: 'Gradient direction (use radio above)',
        default: lit('to bottom'),
      },
      border: { kind: 'color', label: 'Shell border colour', default: lit('rgba(171,174,179,0.24)'), templateDefaults: { sleek: lit('rgba(255,255,255,0.03)') } },
      borderWidth: { kind: 'radius', label: 'Shell border width (px, 0 = hidden)', default: lit(2), templates: NOT_SLEEK },
      // Escape hatch for host apps that draw their own bottom navigation
      // over the iframe: margin lifts the whole mobile rail, padding grows
      // the shell fill below the controls. Desktop rail sits in the sidebar
      // and is unaffected.
      marginBottom: {
        kind: 'radius',
        label: 'Bar offset',
        hint: 'Mobile bar only — lifts the whole bar up off the screen bottom (px). The gap under it stays transparent. Use when the host app draws its own bottom navigation over the iframe.',
        default: lit(0),
      },
      paddingBottom: {
        kind: 'radius',
        label: 'Bar padding',
        hint: 'Mobile bar only — grows the shell fill below the buttons (px); the bar itself still touches the screen bottom, so the host app UI overlaps solid shell instead of the RIP button.',
        default: lit(0),
      },
      // One height for the sleek dock's stepper and Buy button, so they stay
      // level (partner Loom 2026-10-14); the dock and the lobby fit follow it.
      buttonHeight: {
        kind: 'radius',
        label: 'Button height (stepper + Buy, px)',
        hint: 'Height of the quantity stepper and the Buy button together. The dock grows with them and the pack above shrinks to keep the lobby on one screen.',
        default: lit(52),
        templates: ['sleek'],
      },
      // The mobile rail's shadow is a `filter: drop-shadow()` — it has to
      // follow the silhouette of the two clip-path shell shapes, which a
      // box-shadow (a rectangle around the wrapper) cannot. That rules out
      // `kind: 'shadow'` here: its editor speaks box-shadow syntax, which
      // would make `filter` invalid. Hence the three parameters of
      // `drop-shadow()` as separate knobs.
      shadowColor: {
        kind: 'color',
        label: 'Bar shadow colour',
        hint: 'Mobile bar only — the shadow the bar casts up over the page. Set to transparent to remove the shadow entirely, e.g. when the host app draws its own bottom navigation right under the bar.',
        default: lit('rgba(0,0,0,0.18)'),
        templateDefaults: { sleek: lit('transparent') },
      },
      shadowOffsetY: {
        kind: 'radius',
        label: 'Bar shadow offset Y',
        hint: 'Mobile bar only — how far the shadow is shifted vertically (px). Negative casts it upward over the page, 0 keeps it centred behind the bar.',
        default: lit(-6),
      },
      shadowBlur: {
        kind: 'radius',
        label: 'Bar shadow blur',
        hint: 'Mobile bar only — shadow softness (px). 0 gives a hard edge.',
        default: lit(20),
      },
    },
  },
  {
    name: 'CardModal',
    displayName: 'Card modal (preview)',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title size (px)', default: lit(26) },
      labelColor: { kind: 'color', label: 'Label chip text', default: lit('#FFFFFF') },
      labelSize: { kind: 'fontSize', label: 'Label chip size (px)', default: lit(14) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = backdrop visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      backdropTint: {
        kind: 'color',
        label: 'Backdrop tint (page behind modal)',
        default: lit('rgba(0,0,0,0.60)'),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'PackInfoCard',
    displayName: 'Pack info card (sidebar)',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family (title + subtitle)',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title size (px)', default: lit(24), templateDefaults: { sleek: lit(20) } },
      subtitleColor: { kind: 'color', label: 'Subtitle colour', default: lit('rgba(255,255,255,0.8)'), templateDefaults: { sleek: lit('#6D707C') } },
      subtitleSize: { kind: 'fontSize', label: 'Subtitle size (px)', default: lit(13), templateDefaults: { sleek: lit(14) } },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)'), templateDefaults: { sleek: lit('#1A1B1F') } },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)'), templateDefaults: { sleek: lit('#1A1B1F') } },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
        templateDefaults: { sleek: lit(0) },
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)'), templateDefaults: { sleek: lit('rgba(255,255,255,0.01)') } },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
        templateDefaults: { sleek: lit(1) },
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28), templateDefaults: { sleek: lit(24) } },
    },
  },
  {
    name: 'TierCard',
    displayName: 'Tier card (Pro / Premier / …)',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'gradientDirection',
    variantLabel: 'Background gradient direction',
    knobs: {
      titleText: { kind: 'color', label: 'Tier name colour', default: lit('#FFFFFF') },
      priceText: { kind: 'color', label: 'Price colour', default: lit('#FFFFFF') },
      gradientFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(191,191,191,0.13)'), templateDefaults: { sleek: lit('#25262C') } },
      gradientTo: { kind: 'color', label: 'Background — end', default: lit('rgba(0,0,0,0.06)'), templateDefaults: { sleek: lit('#25262C') } },
      gradientDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      // Active-card background — falls back to the shared gradient when
      // unset, so existing themes keep painting active + inactive alike.
      // Direction stays shared (`gradientDirection`) for both states.
      activeGradientFrom: {
        kind: 'color',
        label: 'Active background — start',
        default: inherit('TierCard', 'gradientFrom'),
      },
      activeGradientTo: {
        kind: 'color',
        label: 'Active background — end',
        default: inherit('TierCard', 'gradientTo'),
      },
      activeBorder: { kind: 'color', label: 'Active border colour', default: lit('#23D11D'), templateDefaults: { sleek: lit('#FFFFFF') } },
      activeBorderWidth: {
        kind: 'radius',
        label: 'Active border width (px, 0 = hidden)',
        default: lit(2),
        templateDefaults: { sleek: lit(1) },
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(16) },
    },
  },
  {
    // Scroll-nav arrows at the edges of the tier row (TierSelectorArrow).
    // Defaults mirror the component's CSS fallbacks: a translucent dark
    // pill that reads over the fade mask on light and dark pack artwork.
    name: 'TierSelector',
    displayName: 'Tier selector arrows',
    group: 'Panels',
    scenes: ['pack'],
    knobs: {
      arrowBg: { kind: 'color', label: 'Arrow — background', default: lit('rgba(20,20,20,0.55)') },
      arrowBgHover: {
        kind: 'color',
        label: 'Arrow — background (hover)',
        default: lit('rgba(20,20,20,0.75)'),
      },
      arrowColor: { kind: 'color', label: 'Arrow — icon colour', default: lit('#ffffff') },
      arrowBorder: {
        kind: 'color',
        label: 'Arrow — border colour',
        default: lit('rgba(255,255,255,0.15)'),
      },
      arrowSize: { kind: 'radius', label: 'Arrow — button size (px)', default: lit(28) },
    },
  },
  {
    name: 'QuantityStepper',
    displayName: 'Quantity stepper',
    group: 'Buttons',
    scenes: ['pack'],
    knobs: {
      containerFill: { kind: 'color', label: 'Pod outer fill', default: lit('#FFFFFF'), templateDefaults: { sleek: lit('#1A1B1F') } },
      backgroundFrom: { kind: 'color', label: 'Inner background — start', default: lit('#E0E0E0'), templateDefaults: { sleek: lit('#1A1B1F') } },
      backgroundTo: { kind: 'color', label: 'Inner background — end', default: lit('#FFFFFF'), templateDefaults: { sleek: lit('#1A1B1F') } },
      buttonFill: { kind: 'color', label: '± button fill', default: lit('#FFFFFF'), templateDefaults: { sleek: lit('#2F3037') } },
      buttonIcon: { kind: 'color', label: '± icon colour', default: lit('#15181E'), templateDefaults: { sleek: lit('#FFFFFF') } },
      valueText: { kind: 'color', label: 'Number colour', default: lit('#15181E'), templateDefaults: { sleek: lit('#FFFFFF') } },
      buttonBorder: { kind: 'color', label: '± button border (sleek)', default: lit('rgba(255,255,255,0.08)'), templateDefaults: { sleek: lit('transparent') } },
      innerBorder: { kind: 'color', label: 'Inner surface border (sleek)', default: lit('rgba(255,255,255,0.06)'), templateDefaults: { sleek: lit('transparent') } },
      // Value must stay a CSS display keyword; it is written straight onto
      // the stepper. Hidden, the purchase stays at one pack.
      display: {
        kind: 'text',
        label: 'Display (none = hidden)',
        hint: 'Hide the stepper to sell one pack per RIP. Applies to mobile and desktop.',
        default: lit('block'),
        options: ['block', 'none'] as const,
        templates: ['modern', 'glass'],
      },
    },
  },
  {
    name: 'BuyButton',
    displayName: 'Buy button (sleek dock)',
    group: 'Buttons',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#F4F4F5') },
      text: { kind: 'color', label: 'Text', default: lit('#0E0F11') },
      disabledFill: { kind: 'color', label: 'Disabled fill', default: lit('#1A1B1F') },
      disabledText: { kind: 'color', label: 'Disabled text', default: lit('#2F3037') },
    },
  },
  {
    // Over the pack art while the pack is restocking (partner: "Sold out").
    name: 'SoldOutBadge',
    displayName: 'Sold out badge on the pack (sleek)',
    group: 'Badges',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#0E0F11') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(7) },
      shadow: { kind: 'shadow', label: 'Shadow', default: lit('0 0 20px rgba(0,0,0,0.5)') },
    },
  },
  {
    name: 'MyPacksPill',
    displayName: 'Vault pill (sleek)',
    group: 'Buttons',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      // Key kept from the old "new activity" dot, so a saved colour carries over to the badge.
      indicatorColor: { kind: 'color', label: 'Card count badge — fill', default: lit('#FF3B30') },
      indicatorText: { kind: 'color', label: 'Card count badge — number', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'SleekHeaderPill',
    displayName: 'Header pills — surface (sleek)',
    group: 'Buttons',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.01)') },
      text: { kind: 'color', label: 'Text & icons', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'SleekHeaderBar',
    displayName: 'Header bar on scroll (sleek)',
    group: 'Layout',
    templates: ['sleek'],
    // The Custom Pack Lab has a header of its own (LabHeader).
    excludeScenes: ['custom-pack'],
    knobs: {
      scrolledFill: { kind: 'color', label: 'Fill', default: lit('rgba(0,0,0,0.4)') },
      scrolledBorder: { kind: 'color', label: 'Bottom border', default: lit('rgba(255,255,255,0.1)') },
      scrolledBlur: { kind: 'radius', label: 'Blur (px, 0 = no blur)', default: lit(12) },
    },
  },
  {
    name: 'MyPacksVaultHint',
    displayName: 'Vault — 24h hold hint (sleek)',
    group: 'Misc',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      color: { kind: 'color', label: 'Text', default: lit('#6D707C') },
    },
  },
  {
    name: 'MyPacksTabs',
    displayName: 'My Packs tabs (sleek)',
    group: 'Misc',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      activeText: { kind: 'color', label: 'Active tab text', default: lit('#FFFFFF') },
      idleText: { kind: 'color', label: 'Idle tab text', default: lit('#6D707C') },
      indicator: { kind: 'color', label: 'Active underline', default: lit('#FFFFFF') },
      divider: { kind: 'color', label: 'Divider under tabs', default: lit('rgba(255,255,255,0.03)') },
    },
  },
  {
    name: 'RevealCloseButton',
    displayName: 'Reveal X / exit (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      icon: { kind: 'color', label: 'Icon', default: lit('#FFFFFF') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.01)') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(60) },
    },
  },
  {
    name: 'RevealRipButton',
    displayName: 'Reveal Rip / Skip (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'RevealVaultButton',
    displayName: 'Reveal Vault (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'RevealSellButton',
    displayName: 'Reveal Sell for N% (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'RevealNextPackButton',
    displayName: 'Reveal Rip next pack (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'RevealDoneButton',
    displayName: 'Reveal Done — back to My Packs (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'RevealVaultedLine',
    displayName: 'Winner screen — "Vaulted" (sleek)',
    group: 'Reveal',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      color: { kind: 'color', label: 'Colour', default: lit('#6D707C') },
    },
  },
  {
    name: 'RevealSwipeHint',
    displayName: 'Reveal — swipe hint above the pack (sleek)',
    group: 'Reveal',
    scenes: ['reveal', 'custom-pack'],
    section: 'Reveal',
    templates: ['sleek'],
    knobs: {
      color: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      // The slice mechanic's "Swipe to slice pack open" (partner Figma
      // 2026-10-14). A CSS display keyword: written straight onto the line.
      display: {
        kind: 'text',
        label: 'Slice hint — display (none = hidden)',
        hint: 'The "Swipe to slice pack open" line above a sealed pack, until the player starts the cut.',
        default: lit('block'),
        options: ['block', 'none'] as const,
      },
    },
  },
  {
    name: 'RevealPackProgress',
    displayName: 'Reveal top bar — "Pack 1/2" (sleek)',
    group: 'Reveal',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      color: { kind: 'color', label: 'Colour', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'RevealExitSheet',
    displayName: 'Reveal — unopened packs sheet (sleek)',
    group: 'Panels',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      background: { kind: 'color', label: 'Sheet background', default: lit('#0E0F11') },
      borderRadius: { kind: 'radius', label: 'Top corner radius (px)', default: lit(24) },
      handle: { kind: 'color', label: 'Drag handle', default: lit('#2F3037') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      mutedText: { kind: 'color', label: 'Muted text', default: lit('#6D707C') },
    },
  },
  {
    name: 'RevealCloseAndVaultButton',
    displayName: 'Unopened packs sheet — Close and vault (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'VaultEmptyStateButton',
    displayName: 'Vault empty state — Buy packs (sleek)',
    group: 'Buttons',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#F4F4F5') },
      text: { kind: 'color', label: 'Text', default: lit('#0E0F11') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(100) },
    },
  },
  {
    name: 'RevealContinueRippingButton',
    displayName: 'Unopened packs sheet — Continue (sleek)',
    group: 'Buttons',
    scenes: ['reveal'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'MyPacksSellNowButton',
    displayName: 'My Packs card Sell for N% value (sleek)',
    group: 'Buttons',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#F4F4F5') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(8) },
    },
  },
  {
    name: 'MyPacksShipButton',
    displayName: 'My Packs Select all / Ship / Deselect all (sleek)',
    group: 'Buttons',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'MyPacksSellButton',
    displayName: 'My Packs Sell selected (sleek)',
    group: 'Buttons',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'ShippingPrimaryButton',
    displayName: 'Shipping sheet — Confirm / Add Delivery (sleek)',
    group: 'Buttons',
    scenes: ['reveal', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Text', default: lit('#101114') },
      disabledFill: { kind: 'color', label: 'Disabled fill', default: lit('#1A1B1F') },
      disabledText: { kind: 'color', label: 'Disabled text', default: lit('#43444F') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'ShipAddAddressButton',
    displayName: 'Ship Card — Add address (sleek)',
    group: 'Buttons',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#9333EA') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(10) },
    },
  },
  {
    name: 'ShipCard',
    displayName: 'Ship Card page (sleek)',
    group: 'Panels',
    scenes: ['my-packs'],
    templates: ['sleek'],
    knobs: {
      titleColor: { kind: 'color', label: 'Titles and values', default: lit('#FFFFFF') },
      mutedText: { kind: 'color', label: 'Muted text', default: lit('#6D707C') },
      divider: { kind: 'color', label: 'Section dividers', default: lit('rgba(255,255,255,0.03)') },
      rowFill: { kind: 'color', label: 'Option row fill', default: lit('#1A1B1F') },
      rowBorder: { kind: 'color', label: 'Option row border', default: lit('rgba(255,255,255,0.01)') },
      selectedBorder: { kind: 'color', label: 'Picked option outline', default: lit('#FFFFFF') },
      linkColor: { kind: 'color', label: 'Edit address link', default: lit('#C084FC') },
      errorText: { kind: 'color', label: 'Error text', default: lit('#F87171') },
      sheetFill: { kind: 'color', label: 'Cancel delivery sheet — fill', default: lit('#0E0F11') },
      sheetHandle: { kind: 'color', label: 'Cancel delivery sheet — drag handle', default: lit('#2F3037') },
    },
  },
  {
    name: 'ShippingSecondaryButton',
    displayName: 'Shipping sheet — Sell for N% (sleek)',
    group: 'Buttons',
    scenes: ['reveal', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#25262C') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'OddsSheet',
    displayName: 'Odds picker (sleek)',
    group: 'Panels',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      background: { kind: 'color', label: 'Sheet background', default: lit('#0E0F11') },
      borderRadius: { kind: 'radius', label: 'Top corner radius (px)', default: lit(24) },
      handle: { kind: 'color', label: 'Drag handle', default: lit('#2F3037') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      mutedText: { kind: 'color', label: 'Muted text', default: lit('#6D707C') },
      surface: { kind: 'color', label: 'Idle tab / bar track / Close fill', default: lit('#1A1B1F') },
      barFill: {
        kind: 'color',
        label: 'Bar fill — every rarity',
        hint: 'One colour for all bars. Leave empty to keep the per-rarity colours from "Rarity colours (sleek)".',
        default: inherit('SleekRarity', 'common'),
      },
      tabActiveFill: { kind: 'color', label: 'Selected tab fill', default: lit('#FFFFFF') },
      tabActiveText: { kind: 'color', label: 'Selected tab text', default: lit('#101114') },
      tabBorder: { kind: 'color', label: 'Tab border', default: lit('rgba(255,255,255,0.01)') },
      applyFill: { kind: 'color', label: 'Apply button fill', default: lit('#FFFFFF') },
      applyText: { kind: 'color', label: 'Apply button text', default: lit('#101114') },
    },
  },
  {
    name: 'SleekRarity',
    displayName: 'Rarity colours — odds bars, chip & glow (sleek)',
    group: 'Misc',
    scenes: ['pack', 'reveal', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      common: { kind: 'color', label: 'Common', default: lit('#6D707C') },
      uncommon: { kind: 'color', label: 'Uncommon', default: lit('#97D926') },
      rare: { kind: 'color', label: 'Rare', default: lit('#0DA7F2') },
      epic: { kind: 'color', label: 'Epic', default: lit('#DF203C') },
      legendary: { kind: 'color', label: 'Legendary', default: lit('#F2830D') },
      grail: { kind: 'color', label: 'Grail', default: lit('#F2B50D') },
      mythic: { kind: 'color', label: 'Mythic', default: lit('#8442F0') },
      chipText: { kind: 'color', label: 'Chip text', default: lit('#FFFFFF') },
      chipFillOpacity: { kind: 'opacity', label: 'Chip tint strength (0–1)', default: lit(0.2) },
      chipBorder: { kind: 'color', label: 'Chip border', default: lit('rgba(255,255,255,0.01)') },
    },
  },
  {
    name: 'WhatsInside',
    displayName: "What's inside page",
    group: 'Panels',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'WhatsInsideButton',
    displayName: "What's inside — lobby pill (sleek)",
    group: 'Buttons',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.01)') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(60) },
    },
  },
  {
    name: 'SleekBackButton',
    displayName: 'Back arrow — What\'s inside, Vault (sleek)',
    group: 'Buttons',
    scenes: ['pack', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.01)') },
      icon: { kind: 'color', label: 'Icon', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(60) },
    },
  },
  {
    name: 'WhatsInsideItem',
    displayName: "What's inside tile",
    group: 'Panels',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      background: { kind: 'color', label: 'Tile background', default: lit('#1A1B1F') },
      borderColor: { kind: 'color', label: 'Tile border', default: lit('rgba(255,255,255,0.01)') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(18) },
      priceColor: { kind: 'color', label: 'Price', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'WhatsInsideSheet',
    displayName: "What's inside card sheet (sleek)",
    group: 'Panels',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      background: { kind: 'color', label: 'Sheet background', default: lit('#0E0F11') },
      borderRadius: { kind: 'radius', label: 'Top corner radius (px)', default: lit(24) },
      handle: { kind: 'color', label: 'Drag handle', default: lit('#2F3037') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      mutedText: { kind: 'color', label: 'Muted text', default: lit('#6D707C') },
    },
  },
  {
    name: 'WhatsInsideCloseButton',
    displayName: "What's inside sheet — Close (sleek)",
    group: 'Buttons',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(12) },
    },
  },
  {
    name: 'FairnessSheet',
    displayName: 'Provably fair sheet (sleek)',
    group: 'Panels',
    scenes: ['pack', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      background: { kind: 'color', label: 'Sheet background', default: lit('#0E0F11') },
      borderRadius: { kind: 'radius', label: 'Top corner radius (px)', default: lit(24) },
      handle: { kind: 'color', label: 'Drag handle', default: lit('#2F3037') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      mutedText: { kind: 'color', label: 'Muted text', default: lit('#6D707C') },
      surface: { kind: 'color', label: 'Seed field / roll card fill', default: lit('#1A1B1F') },
    },
  },
  {
    name: 'FairnessLearnMoreButton',
    displayName: 'Provably fair — Learn More pill (sleek)',
    group: 'Buttons',
    scenes: ['pack'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.16)') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(60) },
    },
  },
  {
    name: 'FairnessCloseButton',
    displayName: 'Provably fair sheet — Close (sleek)',
    group: 'Buttons',
    scenes: ['pack', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#25262C') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.01)') },
      icon: { kind: 'color', label: 'Icon', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(60) },
    },
  },
  {
    name: 'FairnessDoneButton',
    displayName: 'Provably fair sheet — Done (sleek)',
    group: 'Buttons',
    scenes: ['pack', 'my-packs'],
    templates: ['sleek'],
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#1A1B1F') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.16)') },
      text: { kind: 'color', label: 'Text', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Corner radius (px)', default: lit(60) },
    },
  },
  {
    name: 'DisclaimerModal',
    displayName: 'Disclaimer modal',
    group: 'Modals',
    scenes: ['pack', 'my-packs'],
    knobs: {
      // ── Card (white sheet) ────────────────────────────────────────────
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#FFFFFF'),
      },
      borderRadius: {
        kind: 'radius',
        label: 'Sheet — border radius (px)',
        default: lit(24),
        templates: NOT_SLEEK,
      },
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },

      // ── Close (X) button ─────────────────────────────────────────────
      closeFill: { kind: 'color', label: 'Close (X) — fill', default: lit('rgba(0,0,0,0.6)') },
      closeIconColor: { kind: 'color', label: 'Close (X) — icon colour', default: lit('#FFFFFF') },

      // Close (X) button also exposes the shared `glass-frosted` Button variant —
      // theme it via `Button → glass-frosted *` knobs (not duplicated
      // here so partners see a single source of truth for the frosted
      // pill that paints every other close/header chip in the app).

      // ── Title ────────────────────────────────────────────────────────
      titleFont: {
        kind: 'fontFamily',
        label: 'Title — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      titleColor: {
        kind: 'color',
        label: 'Title — colour',
        default: lit('#15181E'),
      },
      bodyColor: {
        kind: 'color',
        label: 'Body — colour',
        default: lit('#15181E'),
      },
      titleSize: {
        kind: 'fontSize',
        label: 'Title — size (px)',
        default: lit(18),
        templates: NOT_SLEEK,
      },

      // ── Description ──────────────────────────────────────────────────
      descFont: {
        kind: 'fontFamily',
        label: 'Description — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      descColor: {
        kind: 'color',
        label: 'Description — colour',
        default: lit('rgba(0,0,0,0.78)'),
        templates: NOT_SLEEK,
      },
      descSize: {
        kind: 'fontSize',
        label: 'Description — size (px)',
        default: lit(15),
        templates: NOT_SLEEK,
      },
      linkColor: {
        kind: 'color',
        label: 'Inline link — colour',
        default: lit('#0066FF'),
      },

      // ── Dismiss button (outline pill) ────────────────────────────────
      dismissFill: {
        kind: 'color',
        label: 'Dismiss button — background',
        default: lit('#FFFFFF'),
      },
      dismissText: {
        kind: 'color',
        label: 'Dismiss button — text',
        default: lit('#15181E'),
      },
      dismissBorder: {
        kind: 'color',
        label: 'Dismiss button — border',
        default: lit('rgba(0,0,0,0.12)'),
      },
      dismissBorderRadius: {
        kind: 'radius',
        label: 'Dismiss button — border radius (px)',
        default: lit(999),
        templates: NOT_SLEEK,
      },
      dismissFont: {
        kind: 'fontFamily',
        label: 'Dismiss button — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
        templates: NOT_SLEEK,
      },
      dismissSize: {
        kind: 'fontSize',
        label: 'Dismiss button — text size (px)',
        default: lit(15),
        templates: NOT_SLEEK,
      },
    },
  },
  {
    name: 'HighValueDisclaimer',
    displayName: 'High-value item disclaimer',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: lit('rgba(255,255,255,0.04)') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.12)') },
      iconColor: { kind: 'color', label: 'Icon colour', default: lit('rgba(255,255,255,0.55)') },
      textColor: { kind: 'color', label: 'Text colour', default: lit('rgba(255,255,255,0.70)') },
    },
  },
  {
    name: 'ProtectionAck',
    displayName: 'Protection opt-in checkbox',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      boxFillChecked: { kind: 'color', label: 'Checkbox — checked fill', default: lit('#23D11D') },
      boxFill: { kind: 'color', label: 'Checkbox — unchecked fill', default: lit('transparent') },
      boxBorder: { kind: 'color', label: 'Checkbox — border', default: lit('rgba(222,222,224,0.29)') },
      checkColor: { kind: 'color', label: 'Checkmark colour', default: lit('#0A0B0C') },
      labelColor: { kind: 'color', label: 'Label text colour', default: lit('rgba(255,255,255,0.85)') },
    },
  },
  {
    name: 'ProtectionOptOut',
    displayName: 'Protection opt-out warning',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: lit('rgba(245,158,11,0.10)') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(245,158,11,0.45)') },
      iconColor: { kind: 'color', label: 'Icon colour', default: lit('#F59E0B') },
      textColor: { kind: 'color', label: 'Text colour', default: lit('rgba(255,255,255,0.90)') },
    },
  },
  {
    name: 'ShippingOptionRequired',
    displayName: 'Shipping option required chip',
    group: 'Misc',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: lit('rgba(35,209,29,0.16)') },
      text: { kind: 'color', label: 'Text colour', default: lit('#23D11D') },
    },
  },
  // The delivery form's fulfilment-queue hint. TWO colours, not one: the same
  // line is a neutral hint while the basket still fits under the operator's
  // cap and an alert once it does not (Submit is blocked in that case), so an
  // operator theming the hint would otherwise silently restyle the warning
  // with it. Defaults are the fallbacks written at the call site, so an
  // untouched theme renders exactly what the code renders today.
  {
    name: 'DeliveryNew',
    displayName: 'Delivery — fulfilment queue hint',
    group: 'Misc',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      quotaHintColor: {
        kind: 'color',
        label: 'Queue hint — text',
        default: lit('rgba(255,255,255,0.55)'),
      },
      quotaOverflowColor: {
        kind: 'color',
        label: 'Queue hint — over the cap',
        default: lit('#F87171'),
      },
    },
  },
  // Replaces the delivery form entirely once the player already has the
  // operator's maximum number of cards awaiting fulfilment. Informational
  // rather than a failure — nothing went wrong, there is simply no room until
  // the pending deliveries ship — which is why it carries its own palette
  // instead of the error one.
  {
    name: 'QuotaFullNotice',
    displayName: 'Delivery — queue full notice',
    group: 'Panels',
    templates: NOT_SLEEK,
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: lit('rgba(255,255,255,0.04)') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.10)') },
      iconColor: { kind: 'color', label: 'Icon', default: lit('#7DD3FC') },
      titleColor: { kind: 'color', label: 'Title — colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(18) },
      textColor: {
        kind: 'color',
        label: 'Message — colour',
        default: lit('rgba(255,255,255,0.70)'),
      },
    },
  },
  {
    name: 'AuthModal',
    displayName: 'Auth modal (embed sign-in)',
    group: 'Modals',
    scenes: ['pack', 'my-packs', 'custom-pack'],
    knobs: {
      // ── Card (white sheet) ────────────────────────────────────────────
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#FFFFFF'),
      },
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: {
        kind: 'color',
        label: 'Title — colour',
        default: lit('#15181E'),
      },

      // ── Close (X) button ─────────────────────────────────────────────
      closeFill: {
        kind: 'color',
        label: 'Close (X) — fill',
        default: lit('rgba(0,0,0,0.6)'),
      },
      closeIconColor: {
        kind: 'color',
        label: 'Close (X) — icon colour',
        default: lit('#FFFFFF'),
      },

      // ── Tabs (Sign in / Register) ────────────────────────────────────
      tabTrackFill: {
        kind: 'color',
        label: 'Tab track — fill',
        default: lit('#F2F2F2'),
      },
      tabActiveFill: {
        kind: 'color',
        label: 'Tab (active) — fill',
        default: lit('#FFFFFF'),
      },
      tabActiveText: {
        kind: 'color',
        label: 'Tab (active) — text',
        default: lit('#15181E'),
      },
      tabActiveShadow: {
        kind: 'shadow',
        label: 'Tab (active) — shadow',
        default: lit('0 1px 4px rgba(0,0,0,0.08)'),
      },
      tabIdleText: {
        kind: 'color',
        label: 'Tab (idle) — text',
        default: lit('#6B7280'),
      },

      // ── Inline text (errors, notes, links) ───────────────────────────
      errorColor: {
        kind: 'color',
        label: 'Error text — colour',
        default: lit('#E5484D'),
      },
      noteColor: {
        kind: 'color',
        label: 'Helper note — colour',
        default: lit('#6B7280'),
      },
      linkColor: {
        kind: 'color',
        label: 'Inline link — colour',
        default: lit('#6B7280'),
      },
    },
  },
  {
    name: 'Deposit',
    displayName: 'Deposit screen (Stripe top-up)',
    group: 'Modals',
    scenes: ['pack', 'my-packs', 'custom-pack'],
    knobs: {
      // ── Card (dark sheet) ─────────────────────────────────────────────
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#15181E'),
      },
      borderRadius: {
        kind: 'radius',
        label: 'Sheet — border radius (px)',
        default: lit(28),
      },
      closeColor: {
        kind: 'color',
        label: 'Close (X) — colour',
        default: lit('rgba(255,255,255,0.55)'),
      },

      // ── Balance pill ──────────────────────────────────────────────────
      pillFill: {
        kind: 'color',
        label: 'Balance pill — fill',
        default: lit('rgba(255,255,255,0.1)'),
      },
      pillRadius: {
        kind: 'radius',
        label: 'Balance pill — border radius (px)',
        default: lit(999),
      },
      pillText: {
        kind: 'color',
        label: 'Balance pill — text',
        default: lit('rgba(255,255,255,0.65)'),
      },

      // ── Amount display ────────────────────────────────────────────────
      amountSize: {
        kind: 'fontSize',
        label: 'Amount — size (px)',
        default: lit(72),
      },
      amountText: {
        kind: 'color',
        label: 'Amount — text colour',
        default: lit('#ffffff'),
      },

      // ── Amount presets + keypad ──────────────────────────────────────
      presetFill: {
        kind: 'color',
        label: 'Preset chip — fill',
        default: lit('rgba(255,255,255,0.08)'),
      },
      presetText: {
        kind: 'color',
        label: 'Preset chip — text',
        default: lit('#ffffff'),
      },
      keyText: {
        kind: 'color',
        label: 'Keypad — key text',
        default: lit('#ffffff'),
      },

      // ── Payment-method select + menu ─────────────────────────────────
      selectFill: {
        kind: 'color',
        label: 'Method select — fill',
        default: lit('rgba(255,255,255,0.10)'),
      },
      selectText: {
        kind: 'color',
        label: 'Method select — text',
        default: lit('#ffffff'),
      },
      selectBorder: {
        kind: 'color',
        label: 'Input / select — border',
        default: lit('rgba(255,255,255,0.14)'),
      },
      menuFill: {
        kind: 'color',
        label: 'Method menu — fill',
        default: lit('#20242c'),
      },
      menuActiveFill: {
        kind: 'color',
        label: 'Method menu — active row fill',
        default: lit('rgba(255,255,255,0.08)'),
      },

      // ── Card entry fields ─────────────────────────────────────────────
      labelColor: {
        kind: 'color',
        label: 'Field label — colour',
        default: lit('rgba(255,255,255,0.55)'),
      },
      inputFill: {
        kind: 'color',
        label: 'Input — fill',
        default: lit('#1b1f26'),
      },
      focusRing: {
        kind: 'color',
        label: 'Input — focus ring colour',
        default: lit('rgba(34,225,28,0.35)'),
      },

      // ── Footer (withdraw + terms) ────────────────────────────────────
      withdrawColor: {
        kind: 'color',
        label: 'Withdraw link — colour',
        default: lit('rgba(255,255,255,0.55)'),
      },
      termsColor: {
        kind: 'color',
        label: 'Terms — colour',
        default: lit('rgba(255,255,255,0.35)'),
      },
      termsLinkColor: {
        kind: 'color',
        label: 'Terms link — colour',
        default: lit('rgba(255,255,255,0.5)'),
      },
    },
  },
  {
    name: 'Withdraw',
    displayName: 'Withdraw screen',
    group: 'Modals',
    scenes: ['pack', 'my-packs', 'custom-pack'],
    knobs: {
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#15181E'),
      },
      borderRadius: {
        kind: 'radius',
        label: 'Sheet — border radius (px)',
        default: lit(28),
      },
      closeColor: {
        kind: 'color',
        label: 'Close (X) — colour',
        default: lit('rgba(255,255,255,0.55)'),
      },
    },
  },
  {
    name: 'WithdrawAction',
    displayName: 'Withdraw — action required screen',
    group: 'Modals',
    scenes: ['pack', 'my-packs', 'custom-pack'],
    knobs: {
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#15181E'),
      },
      borderRadius: {
        kind: 'radius',
        label: 'Sheet — border radius (px)',
        default: lit(28),
      },
      iconFill: {
        kind: 'color',
        label: 'Icon badge — fill',
        default: lit('#22E11C'),
      },
      iconColor: {
        kind: 'color',
        label: 'Icon badge — colour',
        default: lit('#22E11C'),
      },
      titleColor: {
        kind: 'color',
        label: 'Title — colour',
        default: lit('#ffffff'),
      },
      bodyColor: {
        kind: 'color',
        label: 'Body — colour',
        default: lit('rgba(255,255,255,0.6)'),
      },
    },
  },
  {
    name: 'WithdrawSuccess',
    displayName: 'Withdraw — success screen',
    group: 'Modals',
    scenes: ['pack', 'my-packs', 'custom-pack'],
    knobs: {
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#15181E'),
      },
      borderRadius: {
        kind: 'radius',
        label: 'Sheet — border radius (px)',
        default: lit(28),
      },
      iconFill: {
        kind: 'color',
        label: 'Icon badge — fill',
        default: lit('#22E11C'),
      },
      iconColor: {
        kind: 'color',
        label: 'Icon badge — colour',
        default: lit('#22E11C'),
      },
      titleColor: {
        kind: 'color',
        label: 'Title — colour',
        default: lit('#ffffff'),
      },
      bodyColor: {
        kind: 'color',
        label: 'Body — colour',
        default: lit('rgba(255,255,255,0.6)'),
      },
      linkColor: {
        kind: 'color',
        label: 'Inline link — colour',
        default: lit('#22E11C'),
      },
    },
  },
  {
    name: 'BalancePill',
    displayName: 'Balance pill',
    group: 'Misc',
    scenes: ['pack', 'reveal'],
    knobs: {
      creditGlow: {
        kind: 'color',
        label: 'Credit flash — glow colour',
        default: role('primary'),
      },
      // Both knobs feed `display` on the pill, so their values must stay CSS
      // display keywords — same trick as PackPageBackground.sonarDisplay.
      // Separate knobs per render site: a casino whose own top bar shows the
      // balance still gets to keep (or drop) it during the reveal.
      headerDisplay: {
        kind: 'text',
        label: 'Pack header — pill display (none = hide)',
        hint: 'Hide the balance in the pack page header when the host app draws its own balance above the iframe. On Stripe-funded operators this pill is also the way into the top-up sheet — hiding it leaves "Add funds" in the purchase overlay as the only entry.',
        default: lit('inline-flex'),
        options: ['inline-flex', 'none'] as const,
      },
      revealDisplay: {
        kind: 'text',
        label: 'Reveal screen — pill display (none = hide)',
        hint: 'Hide the balance that sits next to Exit while a pack is being opened. Exit stays flush with the right edge either way.',
        default: lit('inline-flex'),
        options: ['inline-flex', 'none'] as const,
        templates: NOT_SLEEK,
      },
      // A CSS display keyword like the two above: the amount is always
      // formatted with commas and this knob shows or hides them.
      thousandsSeparator: {
        kind: 'text',
        label: 'Thousands separator (none = hide)',
        hint: 'Switch to inline-block to print the balance as $47,750.63 instead of $47750.63. Off by default — the comma is a US convention.',
        default: lit('none'),
        options: ['none', 'inline-block'] as const,
      },
    },
  },
  {
    name: 'HelpLink',
    displayName: 'Help link (support site)',
    group: 'Misc',
    scenes: ['pack', 'my-packs'],
    knobs: {
      // The whole point of the entry: the control ships hidden, because the
      // support site it opens is one URL for the deploy and not every
      // operator runs one. One knob governs all three render sites — pack
      // page desktop (under the RIP rail), pack page mobile (under Potential
      // Hits) and the My Packs header. Value must stay a CSS display
      // keyword; it is written straight onto the anchor.
      display: {
        kind: 'text',
        label: 'Display (none = hidden)',
        hint: 'Off by default. Switch to inline-flex to show a "Help" link that opens the support / FAQ site in a new tab — under the RIP button on desktop, below Potential Hits on mobile, and next to Log out in the My Packs header.',
        default: lit('none'),
        options: ['none', 'inline-flex'] as const,
      },
      fill: {
        kind: 'color',
        label: 'Background',
        default: lit('rgba(255,255,255,0.1)'),
      },
      border: {
        kind: 'color',
        label: 'Border colour',
        default: lit('rgba(255,255,255,0.25)'),
      },
      text: {
        kind: 'color',
        label: 'Label + icon colour',
        default: lit('#FFFFFF'),
      },
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      fontSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(15) },
    },
  },
  ...LAB_COMPONENTS,
];

export function findComponent(name: string): ComponentSchema | undefined {
  return COMPONENTS.find((c) => c.name === name);
}

export function findKnob(
  componentName: string,
  knobName: string,
): KnobDef | undefined {
  return findComponent(componentName)?.knobs[knobName];
}
